import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAdmin,
  LockDetails,
  FundingPlan,
  MAX_LOCK_NAME_BYTES,
  lockNameByteLength,
} from '@/hooks/useAdmin';
import { useGrantPages } from '@/hooks/useGrantPages';
import { useWeb3, Category, categoryLabel } from '@/hooks/useWeb3';
import { usePublicClient } from 'wagmi';
import { environment } from '@/config/environment';
import { TOKEN_LOCK_FACTORY_ABI } from '@/contracts/TokenLockFactory.abi';
import { ERC20_ABI } from '@/contracts/ERC20.abi';
import { displayedVestedAmount, payableCeiling } from '@/contracts/readState';
import { ConfirmModal } from '@/components/ConfirmModal';
import { NotificationModal } from '@/components/NotificationModal';
import { Icon, ICONS } from '@/components/brand';
import { parseTxError } from '@/utils/txError';
import { toast } from 'react-toastify';
import type { Address } from 'viem';

const formatDateTime = (d: Date) =>
  d.toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDay = (d: Date) =>
  d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });

// datetime-local wants local wall-clock, so this cannot go through toISOString.
const toDatetimeLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
};

// The honest ceiling for a row. Always this, never details.maxEntitlement:
// readState falls that field back to totalAmount on a v7/v9 factory, which has no
// ceiling of its own to report, and the clamp is what keeps this panel agreeing
// with the beneficiary's own screen about an off-boarded grant.
const ceilingOf = (details: LockDetails): bigint => payableCeiling(details, Date.now());

// How far a grant has been paid BEYOND what it can still be worth.
//
// Measured against ceilingOf, NEVER against details.totalAmount. The allocation
// stops being the ceiling the moment a grant is frozen, and measuring against it
// is the exact admin-vs-beneficiary divergence payableCeiling was extracted into
// readState to end. Measured on chain: a 1,000-token grant with 375 claimed,
// resized to 600 and then off-boarded, reads totalAmount 600, claimed 375,
// ceiling 225. Against the allocation that is zero and the row printed the GREEN
// "Nothing further due" pill; against the ceiling it is the 150 the
// beneficiary's own screen has been reporting all along.
//
// There are therefore two ways in, not one. A downward resize that lands under
// what had already been claimed — which setTotalAmount allows on purpose, so a
// grant entered as 1,000 and really worth 300 can be recorded as 300 even though
// 500 has gone out — and an off-board, which freezes a DATE rather than an
// amount, so its frozen figure still scales with totalAmount and can sit below
// what has been paid without anyone touching the amount again. Tokens already
// paid are not recalled by arithmetic; this is the figure emergencyWithdrawFrom
// would have to go and get.
//
// Cancellation is excluded rather than clamped. A cancelled grant's ceiling is
// deliberately zero, so every cancelled grant that was ever claimed against
// would otherwise read as overpaid by whatever the beneficiary legitimately took
// before the clawback — which is not an overpayment and has its own pill.
const overpaidBy = (details: LockDetails): bigint => {
  if (details.cancelled) return BigInt(0);
  const ceiling = ceilingOf(details);
  return details.unlockedAmount > ceiling ? details.unlockedAmount - ceiling : BigInt(0);
};

/**
 * What resizing a grant to `next` actually does to it, computed the way the
 * contract computes it rather than described in prose.
 *
 * setTotalAmount moves the SCHEDULE and never the ledger. unlockedAmount is
 * untouched, and both curves are homogeneous of degree one in totalAmount, so
 * the whole entitlement curve rescales by next/current — THE PAST INCLUDED —
 * and earnedUnclaimed re-reads as `entitled(t) - unlockedAmount` from the next
 * block. Every consequence an operator has to be told before signing falls out
 * of that single fact, in both directions, so they are all derived here.
 *
 * These are display figures only — the rescale is applied to the curve values
 * the lock last reported, so they can differ from the chain by a wei of integer
 * division and by whatever the schedule releases between the preview and the
 * block. Nothing here is ever used as a transaction argument: the only thing
 * signed is the new total the operator typed.
 */
const projectResize = (details: LockDetails, next: bigint) => {
  const current = details.totalAmount;
  const claimed = details.unlockedAmount;
  const scale = (value: bigint) => (current > BigInt(0) ? (value * next) / current : BigInt(0));

  // entitledAmount() on chain is min(vesting curve, unlock curve), and
  // getLockState reports both of those raw figures.
  const entitledNow =
    details.vestedAmount < details.unlockScheduleAmount
      ? details.vestedAmount
      : details.unlockScheduleAmount;
  const entitledAfter = scale(entitledNow);

  // earnedUnclaimed, before and after. Both clamp at zero, exactly as the
  // contract does, which is what makes the overpaid state safe to render.
  const owedNow = entitledNow > claimed ? entitledNow - claimed : BigInt(0);
  const owedAfter = entitledAfter > claimed ? entitledAfter - claimed : BigInt(0);

  // Funding only ever pushes, so a cut leaves whatever the lock already holds
  // above its new ceiling sitting there as excessFunding() — recoverable with
  // Return excess, which is why setTotalAmount has no balance precondition.
  const ceilingAfter = scale(ceilingOf(details));
  const stillPayableAfter = ceilingAfter > claimed ? ceilingAfter - claimed : BigInt(0);
  const strandedAfter =
    details.lockBalance > stillPayableAfter ? details.lockBalance - stillPayableAfter : BigInt(0);

  return {
    claimed,
    entitledNow,
    entitledAfter,
    owedNow,
    owedAfter,
    /** Owed the moment this is mined, over and above what was already owed. */
    becomesOwed: owedAfter > owedNow ? owedAfter - owedNow : BigInt(0),
    /** How far the rescaled curve sits BEHIND what has already been paid. */
    behindBy: claimed > entitledAfter ? claimed - entitledAfter : BigInt(0),
    /** The most this grant could ever pay once the resize lands. */
    ceilingAfter,
    /**
     * Non-zero when the rescaled CEILING lands under what has already been paid
     * out. Against `next` this carried the same mistake overpaidBy above did: on
     * an off-boarded grant the ceiling is the frozen curve, which rescales with
     * totalAmount just like everything else, so it sits below the new total and
     * a cut that is genuinely into overpayment previewed as an ordinary one.
     * `ceilingAfter` is `next` exactly on any grant that is not frozen.
     */
    overpaidBy: claimed > ceilingAfter ? claimed - ceilingAfter : BigInt(0),
    /** Newly stranded in the lock by this cut, on top of any existing excess. */
    newlyStranded:
      strandedAfter > details.excessFunding
        ? strandedAfter - details.excessFunding
        : BigInt(0),
  };
};

const sameAddress = (a?: string | null, b?: string | null) =>
  Boolean(a && b && a.toLowerCase() === b.toLowerCase());

// A decoded custom error reaches the toast as its raw signature and argument
// words, which says nothing an operator can act on. Translate the ones an owner
// action can actually trip.
const CONTRACT_ERROR_HINTS: Array<[string, string]> = [
  [
    'InvalidStartTime',
    'Start date rejected: choose a date on or after January 1, 2022 (UTC), and no more than 365 days after the current chain time. This restriction applies to every grant, including a wallet’s first grant. Use the beneficiary’s actual start date within that range.',
  ],
  [
    '0xb290253c',
    'Start date rejected: choose a date on or after January 1, 2022 (UTC), and no more than 365 days after the current chain time. This restriction applies to every grant, including a wallet’s first grant. Use the beneficiary’s actual start date within that range.',
  ],
  // Every funding call is quote-then-sign and carries the quote as its cap, so
  // this is what an operator sees when the ground moved under the quote — almost
  // always an allocation edited between the two.
  [
    'FundingExceedsMax',
    'Funding refused: it would move more than the figure you approved, so it moved nothing. An allocation changed between the quote and the signature — most likely someone raised a grant amount, though a tranche unlocking in that same gap does it too. Re-open this action to take a fresh quote, check the new figure is one you accept, and sign that.',
  ],
  [
    'OffboardBelowEarned',
    // This guard is a guard rail against a mistyped date, NOT a wall: it compares the frozen curve against entitledAmount(), and both scale with totalAmount, so lowering the amount first lowers the frontier it defends — at a small enough total, integer division takes it to zero and any retroactive date passes. Cancellation reaches the same outcome in one transaction and says so. Do not let this hint imply a protection the contract does not provide.
    'Off-board date rejected: it falls behind what this beneficiary has already earned. This is a check against a mistyped date, not a protection for the beneficiary — lowering the grant amount first lowers the line this compares against, and cancelling the grant claws back earned-but-unclaimed tokens outright. If the earlier date is what you mean, say so deliberately through those controls rather than by working around this one.',
  ],
  // Cancellation is terminal, so every other owner control on that grant refuses
  // afterwards. Both of these arrive here forwarded from the lock, which is why
  // the factory re-declares them — without that this would be a raw selector.
  [
    'AlreadyCancelled',
    'This grant was already cancelled. Cancellation happens once and its balance was swept in that same transaction, so there is nothing further to cancel.',
  ],
  [
    'LockIsCancelled',
    'This grant is cancelled, and cancellation is terminal: off-boarding, reactivating, changing the amount and reassigning are all refused on it for good. The remedy for a mistaken cancellation is a replacement grant.',
  ],
  [
    'AmountAboveMax',
    'That amount is above the contract maximum of 1e11 tokens. It is an overflow bound rather than a typo bound, so a value this large almost always means the decimals were typed twice — the field takes whole tokens.',
  ],
  ['SameAmount', 'That is already this grant’s amount, so there is nothing to change.'],
  [
    'LockNameTooLong',
    `The grant name is over ${MAX_LOCK_NAME_BYTES} BYTES of UTF-8. The contract counts bytes, not characters: one CJK character is three of them, so the real limit is about 21 such characters.`,
  ],
  [
    'LockNameEmpty',
    'A name is required when a grant is created — it is what identifies which grant this is now that one wallet may hold several. A name can be blanked later with Rename, but not omitted here.',
  ],
  ['SameBeneficiary', 'That wallet already holds this grant, so there is nothing to reassign.'],
  [
    'UnknownLock',
    'This factory has no record of that address, so it refuses to act on it. Only locks this factory created can be managed here — check the lock address.',
  ],
  // Reassignment forwards to TokenLock.changeBeneficiary, which is whenNotPaused
  // on the LOCK. A paused grant therefore has to be unpaused before it can move.
  [
    'EnforcedPause',
    'Paused. Reassigning a grant goes through the lock’s own beneficiary change, which is blocked while that lock is paused — unpause it, reassign, then pause it again if you still need to.',
  ],
];

const contractErrorHint = (err: any): string | null => {
  let text = '';
  let cursor: any = err;
  let depth = 0;
  while (cursor && depth < 6) {
    if (typeof cursor.message === 'string') text += cursor.message;
    if (typeof cursor.shortMessage === 'string') text += cursor.shortMessage;
    cursor = cursor.cause;
    depth++;
  }
  return CONTRACT_ERROR_HINTS.find(([name]) => text.includes(name))?.[1] ?? null;
};

export const AdminPanel: React.FC = () => {
  const admin = useAdmin();
  const web3 = useWeb3();
  const publicClient = usePublicClient();

  const handleCopyFactoryAddress = async () => {
    try {
      await navigator.clipboard.writeText(environment.factoryAddress);
      toast.success('Factory address copied');
    } catch {
      toast.error('Failed to copy address');
    }
  };

  const [isFactoryPaused, setIsFactoryPaused] = useState(false);
  // The expanded lock lives in the URL (/admin/lock/:address) so an operator can
  // link a colleague straight at it. It is only ever compared against allLocks,
  // never handed to a contract call, so a mistyped or wrong-cased address just
  // matches no row instead of needing its own error state.
  const { address: selectedLock = null } = useParams();
  const navigate = useNavigate();
  const grants = useGrantPages({
    addresses: admin.allLocks,
    selectedAddress: selectedLock,
    revision: [environment.chainId, environment.factoryAddress, web3.address,
      admin.dataVersion, admin.incrementalFunding, admin.tgeTime,
      admin.unlockCliffTimes[Category.INVESTOR], admin.unlockCliffTimes[Category.TEAM]].join(':'),
    load: admin.getAllLockDetails,
  });
  const lockDetails = grants.details;
  const changeGrantPage = (page: number) => {
    navigate('/admin', { replace: true });
    grants.setPage(page);
  };
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showReassignForm, setShowReassignForm] = useState(false);

  const [newBeneficiary, setNewBeneficiary] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  // Required at creation: with several grants per wallet the label is what says
  // WHICH grant this is, so it is not decoration and the contract refuses an
  // empty one. Measured in UTF-8 bytes below, never in characters.
  const [newName, setNewName] = useState('');
  // Investor is the common case and matches Category.INVESTOR = 0; the track is
  // immutable once deployed, so the form spells out what each option costs.
  const [newCategory, setNewCategory] = useState<Category>(Category.INVESTOR);

  // The GRANT being reassigned, not its holder. A beneficiary is no longer a
  // unique reference — that is the whole reason changeBeneficiary was renamed —
  // so this form has to name the lock.
  const [reassignLockAddress, setReassignLockAddress] = useState('');
  const [replacementBeneficiary, setReplacementBeneficiary] = useState('');
  useEffect(() => {
    if (!grants.pageAddresses.includes(reassignLockAddress)) setReassignLockAddress('');
  }, [grants.pageAddresses.join(','), reassignLockAddress]);

  const [tgeInput, setTgeInput] = useState('');
  // datetime-local string per track for the unlock cliff form
  const [cliffInputs, setCliffInputs] = useState<Record<number, string>>({});

  // datetime-local string per lock address for the off-board form
  const [offboardInputs, setOffboardInputs] = useState<Record<string, string>>({});
  // Per-lock inputs for the controls in the expanded row. Keyed by lock address
  // so half-filled values survive expanding another row and coming back.
  const [renameInputs, setRenameInputs] = useState<Record<string, string>>({});
  const [amountInputs, setAmountInputs] = useState<Record<string, string>>({});
  // Where a cancellation sweep or an excess recovery sends the tokens. Defaults
  // to the connected owner, which is almost never wrong and is always visible in
  // the confirmation before anything is signed.
  const [recipientInputs, setRecipientInputs] = useState<Record<string, string>>({});

  // Funding desk. The funding date is an argument to the funding call, never
  // stored state on chain, so the operator picks it each time and the contract
  // prices it. Nothing is signed until the quote below has been previewed.
  const [fundThroughInput, setFundThroughInput] = useState('');
  const [fundingPlan, setFundingPlan] = useState<FundingPlan | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [showFundingBreakdown, setShowFundingBreakdown] = useState(false);

  const [newOwnerInput, setNewOwnerInput] = useState('');

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    destructive?: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showNotification = (
    type: 'success' | 'error' | 'warning' | 'info',
    title: string,
    message: string,
  ) => setNotification({ isOpen: true, type, title, message });

  const showTxErrorToast = (err: any, fallbackMsg = 'Transaction failed.') => {
    const parsed = parseTxError(err, fallbackMsg);
    if (parsed.kind === 'rejected') {
      toast.info('Transaction cancelled in wallet.');
    } else {
      toast.error(contractErrorHint(err) ?? parsed.message);
    }
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    destructive = false,
  ) => setConfirmModal({ isOpen: true, title, message, onConfirm, destructive });

  const closeConfirm = () => setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  const closeNotification = () => setNotification((prev) => ({ ...prev, isOpen: false }));

  // Cliff dates are owner-set per deployment, so every mention of one renders the
  // value read from the factory — never a date baked into the app.
  const cliffLabel = (category: Category, unsetLabel = 'Unset') => {
    const cliff = admin.unlockCliffTimes[category];
    return cliff > BigInt(0) ? formatDateTime(new Date(Number(cliff) * 1000)) : unsetLabel;
  };

  // nextFundingDue() answers three different things with one uint256, and each
  // has to read differently to a human:
  //   0            nothing is owed and nothing will ever release again — not 1970
  //   block.timestamp   someone is already short, so the answer is "now"
  //   a future date     the next tranche boundary, clamped to the funding horizon
  // A minute of tolerance absorbs clock skew between the node and the browser so
  // a "fund now" answer never renders as a date that has quietly gone stale.
  const fundingDue = (() => {
    const due = admin.nextFundingDue;
    if (due === BigInt(0)) return { kind: 'none' as const, date: null };
    const date = new Date(Number(due) * 1000);
    if (date.getTime() <= Date.now() + 60_000) return { kind: 'now' as const, date };
    return { kind: 'future' as const, date };
  })();

  // Seed the funding date with the date the contracts say matters next. The
  // operator can move it; nextFundingDue is always inside the accepted horizon,
  // so it is safe to hand straight to the funding call.
  useEffect(() => {
    if (admin.nextFundingDue > BigInt(0) && !fundThroughInput) {
      setFundThroughInput(toDatetimeLocal(new Date(Number(admin.nextFundingDue) * 1000)));
    }
  }, [admin.nextFundingDue]);

  const fundThroughSec = (() => {
    if (!fundThroughInput) return null;
    const ms = new Date(fundThroughInput).getTime();
    return Number.isNaN(ms) ? null : BigInt(Math.floor(ms / 1000));
  })();

  // A quote is only good for the date it was taken at: change the date and the
  // preview has to be re-taken before anything can be signed.
  const previewMatchesDate = Boolean(
    fundingPlan && fundThroughSec !== null && fundingPlan.fundThrough === fundThroughSec,
  );

  // Health under incremental funding is not "armed", it is "funded": someone is
  // short right now, a top-up is due before the next tranche, or the next
  // tranche is already covered.
  const fundingHealth = (d: LockDetails) => {
    // FIRST, ahead of every other test including the v7 branch. A cancelled lock
    // is owed nothing and will never be funded again: nextUnlockTime and
    // fundingDueNextUnlock are both 0 on it, so every branch below would read it
    // as healthy and print the green "Funded through" pill over a dead grant.
    if (d.cancelled) {
      return {
        cls: 'sh-pill-danger',
        text: 'Cancelled',
        title:
          'This grant was cancelled. Everything the beneficiary had not already claimed is forfeit, its balance was swept, and it will never be funded again.',
      };
    }
    // SECOND, ahead of the funding verdicts, because an overpaid lock passes
    // every one of them. It is owed nothing (earnedUnclaimed clamps at zero), so
    // it is never Short, and once its stranded balance has been recovered it is
    // not Over-funded either — which left the green "Funded through" pill over a
    // grant that has already been paid more than it is now worth. An off-boarded
    // one reaches the green "Nothing further due" branch below instead, which is
    // the case this pill was measured missing.
    const overpaid = overpaidBy(d);
    if (overpaid > BigInt(0)) {
      return {
        cls: 'sh-pill-danger',
        text: `Overpaid ${web3.formatTokenAmount(overpaid)}`,
        title:
          'This grant has been paid more than it can ever pay out — either it was resized below what had already been claimed, or off-boarding froze its curve below that figure. Nothing further is payable from it, ever. The tokens are in the beneficiary’s wallet and only emergencyWithdrawFrom can go after them.',
      };
    }
    if (admin.incrementalFunding === false) {
      return {
        cls: d.isInitialized ? 'sh-pill-success' : 'sh-pill-warn',
        text: d.isInitialized ? 'Initialized' : 'Not initialized',
        title: 'This factory funds the full allocation when a lock is initialized.',
      };
    }
    if (d.unfundedEarned > BigInt(0)) {
      return {
        cls: 'sh-pill-danger',
        text: `Short ${web3.formatTokenAmount(d.unfundedEarned)}`,
        title:
          'Earned and unpayable: the beneficiary cannot claim this until a top-up lands. Overdue.',
      };
    }
    // Ahead of every "funded" verdict below. A lock holding more than it can ever
    // pay is not healthy: funding only ever pushes, so this is a ceiling that
    // dropped after a top-up and the treasury is short by exactly this much until
    // returnLockExcess is called. It used to render as the green pill.
    if (d.excessFunding > BigInt(0)) {
      return {
        cls: 'sh-pill-warn',
        text: `Over-funded ${web3.formatTokenAmount(d.excessFunding)}`,
        title:
          'This lock holds more than it can ever pay out. Recover it with Return excess in the expanded row — nothing else can reach it.',
      };
    }
    if (d.nextUnlockTime === BigInt(0)) {
      return {
        cls: 'sh-pill-success',
        text: d.unlockCliffTime === BigInt(0) ? 'Not scheduled' : 'Nothing further due',
        title:
          d.unlockCliffTime === BigInt(0)
            ? 'This track has no unlock date yet, so nothing is scheduled to release.'
            : 'Every tranche this lock will still release is already funded.',
      };
    }
    const next = formatDay(new Date(Number(d.nextUnlockTime) * 1000));
    if (d.fundingDueNextUnlock > BigInt(0)) {
      return {
        cls: 'sh-pill-warn',
        text: `Top-up due ${next}`,
        title: `Needs ${web3.formatTokenAmount(
          d.fundingDueNextUnlock,
        )} SAHARA before the tranche on ${next}.`,
      };
    }
    return {
      cls: 'sh-pill-success',
      text: `Funded through ${next}`,
      title: 'The next tranche is already covered by the tokens sitting in this lock.',
    };
  };

  const isConnectedOwner = sameAddress(web3.address, admin.factoryOwner);
  const isConnectedPendingOwner = sameAddress(web3.address, admin.pendingOwner);

  useEffect(() => {
    const loadFactoryState = async () => {
      if (!publicClient) return;
      try {
        const paused = (await publicClient.readContract({
          address: environment.factoryAddress as Address,
          abi: TOKEN_LOCK_FACTORY_ABI,
          functionName: 'paused',
        })) as boolean;
        setIsFactoryPaused(paused);
      } catch (err) {
        console.error('Error loading factory state:', err);
      }
    };
    loadFactoryState();
  }, [publicClient, admin.isLoading]);

  /**
   * Details for a lock, tolerant of casing.
   *
   * lockDetails is keyed by whatever `getAllLockContracts` returned, and the
   * addresses in a funding plan come back from a different view, so an exact
   * key lookup is one checksum away from silently rendering an empty column.
   */
  const detailsByLower = useMemo(() => {
    const map: Record<string, LockDetails> = {};
    for (const [key, value] of Object.entries(lockDetails)) map[key.toLowerCase()] = value;
    return map;
  }, [lockDetails]);

  const detailsFor = (lockAddress: string): LockDetails | undefined =>
    lockDetails[lockAddress] ?? detailsByLower[lockAddress.toLowerCase()];

  /** The operator label for a grant, or '' when there is none to show. */
  const nameOf = (lockAddress: string): string => (detailsFor(lockAddress)?.name ?? '').trim();

  /**
   * The current page grouped by beneficiary.
   *
   * One wallet may now hold any number of grants, so a flat list of addresses is
   * ambiguous in the one way that matters: an operator about to cancel needs to
   * know they are looking at the seed grant and not the Series A. Grants for the
   * same holder are rendered adjacently and each carries its ordinal.
   *
   * Roster order is otherwise preserved — allLockContracts is append-only and
   * never has anything removed from it, so an index an operator quoted from a
   * funding plan still means the same grant.
   */
  const groupedLocks = useMemo(() => {
    const order: string[] = [];
    const groups = new Map<string, string[]>();
    for (const lockAddress of grants.pageAddresses) {
      // A lock whose details could not be read groups under itself rather than
      // being folded in with every other unreadable lock.
      const holder = (detailsFor(lockAddress)?.beneficiary ?? lockAddress).toLowerCase();
      if (!groups.has(holder)) {
        groups.set(holder, []);
        order.push(holder);
      }
      groups.get(holder)!.push(lockAddress);
    }
    return order.flatMap((holder) => {
      const members = groups.get(holder)!;
      return members.map((lockAddress, index) => ({
        lockAddress,
        ordinal: index + 1,
        groupSize: members.length,
        isGroupStart: index === 0,
      }));
    });
  }, [grants.pageAddresses.join(','), lockDetails]);

  const handlePauseFactory = async () => {
    try {
      await admin.pauseFactory();
      setIsFactoryPaused(true);
      showNotification('success', 'Factory paused', 'New locks cannot be created until unpaused.');
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to pause factory');
    }
  };

  const handleUnpauseFactory = async () => {
    try {
      await admin.unpauseFactory();
      setIsFactoryPaused(false);
      showNotification('success', 'Factory unpaused', 'The factory is active again.');
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to unpause factory');
    }
  };

  // What the contract measures. NEVER newName.length: one CJK character is three
  // UTF-8 bytes, so 64 bytes is about 21 of them, and a form counting characters
  // lets through a label createLock rejects — which surfaces as a roster deploy
  // that stops half way with some grants created and some not.
  const newNameBytes = lockNameByteLength(newName.trim());

  const handleCreateLock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBeneficiary || !newAmount || !newStartDate || !newName.trim()) {
      showNotification(
        'warning',
        'Missing fields',
        'Please fill in the grant name, beneficiary, amount and start date. The name is required: it is what identifies which grant this is.',
      );
      return;
    }
    if (newNameBytes > MAX_LOCK_NAME_BYTES) {
      showNotification(
        'error',
        'Grant name too long',
        `That name is ${newNameBytes} bytes of UTF-8 and the limit is ${MAX_LOCK_NAME_BYTES}. The contract counts bytes, not characters.`,
      );
      return;
    }
    try {
      const startTime = Math.floor(new Date(newStartDate).getTime() / 1000);
      if (!Number.isSafeInteger(startTime) || startTime < 0) {
        showNotification('error', 'Invalid start date', 'Enter a valid start date and time.');
        return;
      }
      // Mirror TokenLock's constructor bounds using chain time, not the browser clock.
      const chainNow = await admin.getChainTimestamp();
      const earliest = 1640995200n; // 2022-01-01 00:00:00 UTC
      const latest = chainNow + 365n * 86400n;
      if (BigInt(startTime) < earliest || BigInt(startTime) > latest) {
        showNotification(
          'error',
          'Start date outside allowed range',
          `Choose a start date between ${formatDateTime(new Date(Number(earliest) * 1000))} ` +
            `and ${formatDateTime(new Date(Number(latest) * 1000))} (your local time). ` +
            'The earliest start is January 1, 2022 (UTC); future dates are limited to 365 days after chain time. ' +
            'An existing grant for this wallet does not change this range.',
        );
        return;
      }
      // Trimmed, because the label is how an operator will identify this grant in
      // every list from here on and leading whitespace is invisible in all of them.
      const name = newName.trim();
      const lockAddress = await admin.createLock(
        newBeneficiary,
        newAmount,
        startTime,
        newCategory,
        name,
      );
      // Name the track back to the admin: it is the one field they can no longer
      // fix. The address comes from this transaction's own receipt — it cannot be
      // looked up afterwards, because this wallet may now hold several grants.
      showNotification(
        'success',
        'Grant created',
        `“${name}” — ${categoryLabel(newCategory)} track, deployed at ${lockAddress ?? 'an address the receipt did not name'}`,
      );
      setNewBeneficiary('');
      setNewAmount('');
      setNewStartDate('');
      setNewName('');
      setNewCategory(Category.INVESTOR);
      setShowCreateForm(false);
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to create grant');
    }
  };

  // Pre-flight for any funding call. It compares the factory balance against the
  // FUNDING REQUIREMENT, never against the locks' full allocations: the factory
  // only ever holds about one period's worth by design, so comparing against the
  // four-year total would block every legitimate top-up.
  const checkFactoryBalance = async (requiredAmount: bigint): Promise<boolean> => {
    if (!publicClient) return false;
    if (requiredAmount === BigInt(0)) return true;
    try {
      const factoryAddress = environment.factoryAddress as Address;
      const tokenAddress = (await publicClient.readContract({
        address: factoryAddress,
        abi: TOKEN_LOCK_FACTORY_ABI,
        functionName: 'token',
      })) as Address;
      const balance = (await publicClient.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [factoryAddress],
      })) as bigint;
      if (balance < requiredAmount) {
        showNotification(
          'error',
          'Factory underfunded',
          `This top-up needs ${web3.formatTokenAmount(requiredAmount)} SAHARA but the factory ` +
            `holds ${web3.formatTokenAmount(balance)}. Wire ` +
            `${web3.formatTokenAmount(requiredAmount - balance)} more from the treasury to the ` +
            `factory, then run it again.`,
        );
        return false;
      }
      return true;
    } catch (err: any) {
      showNotification('error', 'Balance check failed', err.message || 'Failed to check factory balance');
      return false;
    }
  };

  // initializeLock now means "arm and catch up to today", and it is repeatable.
  // What it needs is this lock's shortfall as of now, which is exactly its
  // unfunded earned amount — not its whole allocation.
  // A funding call still arms any lock that is not armed yet -- _fundLock calls
  // initialize() before it moves anything -- so "no tokens to send" only means
  // "nothing will happen" once every lock is armed.
  // Cancelled locks are excluded: arming one changes a flag and nothing else,
  // since it can never be funded or claimed again, so counting it here would
  // offer a catch-up that has genuinely nothing to do.
  //
  // This counts only locks whose details LOADED, and that is a hole rather than
  // a fact — see unreadableLockCount directly below. Never read a zero here as
  // "every lock is armed".
  const unarmedLockCount = useMemo(
    () =>
      admin.allLocks.filter((a) => {
        const d = detailsFor(a);
        return d && !d.isInitialized && !d.cancelled;
      }).length,
    [admin.allLocks, lockDetails],
  );

  // Includes both unreadable grants and pages not visited yet. Never infer
  // that the whole roster is armed from the subset cached by this panel.
  //
  // getAllLockDetails runs with allowFailure and OMITS an unreadable lock by
  // design — a missing row is honest and a zeroed row is not — so the roster and
  // the details table are allowed to disagree in exactly one direction. Every
  // count taken over the details table is therefore a LOWER BOUND, and an
  // unreadable unarmed lock reads as armed.
  //
  // That is only dangerous where a count of zero is used to refuse work. A
  // multicall hole used to make the catch-up toast "every lock is armed and
  // funded" and decline a catch-up that was genuinely needed, which is the
  // failure mode this codebase already refuses elsewhere: the funding aggregates
  // are read from the contract rather than summed from these rows for the same
  // reason. Where the roster is incomplete the panel says so and lets the
  // operator proceed, instead of reassuring them on data it does not have.
  const unreadableLockCount = useMemo(
    () => admin.allLocks.filter((a) => !detailsFor(a)).length,
    [admin.allLocks, lockDetails],
  );

  // Both catch-up calls are now quote-then-sign, the shape every other funding
  // path in this system already had: the factory prices the call, the operator
  // is shown that figure, and it is signed as the SPEND CAP. The contract
  // reverts FundingExceedsMax rather than moving more.
  //
  // The cap is not ceremony. setTotalAmount is legal at any point in a grant's
  // life, so an allocation raised between the quote and the signature made the
  // old uncapped initializeAll move whatever the new figure said — a thousand
  // times the reviewed total in the case that put this here, bounded only by the
  // factory's balance. The cap must therefore be read IMMEDIATELY before
  // signing; a figure carried over from the last roster load is not a quote.
  //
  // Both calls fund through block.timestamp, so the requirement keeps growing
  // while the operator reads the dialog and the wallet mines. No quote can bound
  // that exactly: a tranche unlocking in that gap makes the call revert
  // FundingExceedsMax and move nothing. That is the cap working, not a bug — the
  // hint for it says to re-quote.
  //
  // What the quote MUST NOT do is drift the other way, and that is why "now"
  // here is the chain's own latest block timestamp and never Date.now(). A
  // browser clock behind the chain is the harmless direction: the cap binds and
  // the call reverts. A browser clock AHEAD of the chain is the harmful one — it
  // prices in tranches the chain has not released yet, so the operator reviews
  // and signs a cap LARGER than the true shortfall, and a cap above the
  // requirement cannot bind. The protection is then gone silently, on a machine
  // whose only fault is a fast clock. The latest block's timestamp is at or
  // before the timestamp of the block that mines the transaction, so a quote
  // taken from it can only err in the direction that makes the cap bind.
  //
  // Contrast the dated funding desk below, which needs none of this: the
  // operator names a date and that same date is what goes to the contract.
  //
  // This throws when the chain cannot be reached, and both callers quote inside
  // the try that already reports "could not price this". Falling back to the
  // browser clock would reinstate exactly the drift above.
  const chainNowSeconds = () => admin.getChainTimestamp();

  // Only a v10 factory takes a spend cap on the arm-and-catch-up calls; v7 and
  // v9 expose the uncapped shapes and useAdmin encodes those instead. The quote
  // is still worth showing there — it is what the roster needs right now — but
  // the chain will not hold the call to it, and the confirmation must not claim
  // a ceiling that does not exist.
  const catchUpCapIsEnforceable = environment.factoryAbiVersion === 'v10';

  /** The cap paragraph for a catch-up confirmation, honest about both factories. */
  const catchUpCapNote = (quote: bigint, whatMoves: string) =>
    catchUpCapIsEnforceable
      ? `\n\nThe transaction is capped at ${web3.formatTokenAmount(quote)} SAHARA, quoted from ` +
        `the factory a moment ago against the chain's own clock. If ${whatMoves} before it is ` +
        `mined the call reverts instead of moving more than you are approving here.`
      : `\n\nTHIS FIGURE IS NOT A CAP. The factory at ${environment.factoryAddress} is the ` +
        `${environment.factoryAbiVersion} shape, whose arm-and-catch-up call takes no maximum, so ` +
        `${web3.formatTokenAmount(quote)} SAHARA is what the roster needs as of the latest block ` +
        `and nothing on chain holds the transaction to it. If ${whatMoves} before it is mined, ` +
        `this call moves the larger figure — bounded only by the factory's balance, not by ` +
        `anything you approve here. Do not leave this dialog open, and do not let an allocation ` +
        `be edited while it is.`;

  const handleInitializeLock = async (lockAddress: string, maxTotal: bigint) => {
    try {
      await admin.initializeLock(lockAddress as Address, maxTotal);
      showNotification(
        'success',
        admin.incrementalFunding ? 'Lock topped up' : 'Lock initialized',
        admin.incrementalFunding
          ? 'The lock is armed and funded for everything earned as of today. Future tranches need their own top-up.'
          : 'The full allocation has been funded and initialized.',
      );
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to top up lock');
    }
  };

  const promptInitializeLock = async (lockAddress: string) => {
    const details = detailsFor(lockAddress);

    // A pre-incremental factory has no per-lock quote to take and no cap to
    // honour: it moves the whole allocation, once. Nothing to authorise beyond
    // what the button already says, so it stays a single click.
    if (admin.incrementalFunding === false) {
      const full = details?.totalAmount ?? BigInt(0);
      if (details && !(await checkFactoryBalance(full))) return;
      await handleInitializeLock(lockAddress, full);
      return;
    }

    // The quote, read now. This is both the number the operator authorises and
    // the cap the transaction carries, so it must be one figure, not two.
    let quote: bigint;
    try {
      quote = await admin.getLockFundingRequiredThrough(
        lockAddress as Address,
        await chainNowSeconds(),
      );
    } catch (err: any) {
      showNotification(
        'error',
        'Could not price this top-up',
        err.message || 'Failed to read what this lock needs right now, or what time the chain is on.',
      );
      return;
    }

    // An armed lock with nothing outstanding would fund 0 tokens: the
    // transaction succeeds, costs gas and changes nothing. Say so instead.
    if (quote === BigInt(0) && details && details.isInitialized) {
      toast.info(
        details.nextUnlockTime > BigInt(0)
          ? `Nothing to top up — this lock is funded through its next tranche on ${formatDateTime(
              new Date(Number(details.nextUnlockTime) * 1000),
            )}.`
          : 'Nothing to top up — this lock has nothing further to release.',
      );
      return;
    }

    if (!(await checkFactoryBalance(quote))) return;

    const label = nameOf(lockAddress);
    const who = label ? `“${label}”` : 'this grant';
    showConfirm(
      details?.isInitialized ? 'Top up grant' : 'Arm & fund grant',
      (quote > BigInt(0)
        ? `Push ${web3.formatTokenAmount(quote)} SAHARA from the factory into ${who}, covering ` +
          `everything it has earned as of now?`
        : `Arm ${who}? Nothing is outstanding on it right now, so this moves no tokens — it only ` +
          `marks the lock as started.`) +
        catchUpCapNote(quote, `this grant's amount changes`),
      () => handleInitializeLock(lockAddress, quote),
    );
  };

  const handleInitializeAll = async (maxTotal: bigint) => {
    if (!(await checkFactoryBalance(maxTotal))) return;
    try {
      await admin.initializeAllLocks(maxTotal);
      showNotification(
        'success',
        admin.incrementalFunding ? 'Caught up to today' : 'Locks initialized',
        admin.incrementalFunding
          ? 'Every lock is armed and funded for everything earned so far. Tranches landing later still need a top-up.'
          : 'Every uninitialized lock has received its full allocation.',
      );
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to catch up locks');
    }
  };

  const promptInitializeAll = async () => {
    if (admin.incrementalFunding === false) {
      showConfirm(
        'Initialize all locks',
        `Transfer ${web3.formatTokenAmount(admin.remainingObligation)} SAHARA to fund the full allocation of every uninitialized lock?`,
        () => handleInitializeAll(admin.remainingObligation),
      );
      return;
    }

    // Funding through block.timestamp costs exactly the outstanding shortfall,
    // and totalFundingRequiredThrough(now) is the factory pricing that itself —
    // priced at the chain's "now", never the browser's. See chainNowSeconds.
    let quote: bigint;
    try {
      quote = await admin.getTotalFundingRequiredThrough(await chainNowSeconds());
    } catch (err: any) {
      showNotification(
        'error',
        'Could not price the catch-up',
        err.message || 'Failed to read what the roster needs right now, or what time the chain is on.',
      );
      return;
    }

    // Nothing short and nothing to arm: the call would move no tokens and change
    // no state, so decline it here rather than confirming a no-op transaction.
    //
    // ONLY when the whole roster was readable. `quote` is an on-chain aggregate
    // over every lock and is always complete, but `unarmedLockCount` is taken
    // over the details table, which omits a lock the multicall could not read —
    // so an unreadable unarmed lock reads as armed and this guard used to refuse
    // a catch-up that was genuinely needed while reassuring the operator that
    // there was nothing to do. "I could not read the whole roster" is a
    // different answer from "there is nothing to do", and it is said plainly
    // below instead of being rounded down to the comfortable one.
    if (quote === BigInt(0) && unarmedLockCount === 0 && unreadableLockCount === 0) {
      toast.info('Nothing to catch up — every lock is armed and funded for everything earned so far.');
      return;
    }

    // Stated wherever the panel's own view of the roster is incomplete, so no
    // figure below is read as a full account of it.
    const rosterGap =
      unreadableLockCount > 0
        ? `\n\nTHIS PANEL HAS NOT LOADED ${unreadableLockCount} OF ${admin.allLocks.length} GRANTS ` +
          `in the roster, so it cannot tell you whether they are armed. The figure above is not ` +
          `affected — the factory prices its whole roster in one call and either answers or ` +
          `reverts — but any count of unarmed grants on this page is a lower bound. Arming a lock ` +
          `that is already armed does nothing; visit the other pages or retry missing details ` +
          `to inspect more grants before you sign.`
        : '';

    showConfirm(
      'Catch up to today',
      `Arm every lock and push whatever is earned but unfunded as of now` +
        (quote > BigInt(0)
          ? ` — ${web3.formatTokenAmount(quote)} SAHARA.`
          : `. Nothing is currently short, so this will move no tokens.`) +
        catchUpCapNote(quote, `any allocation changes`) +
        rosterGap +
        `\n\nThis does not cover tranches that land after today; use the funding date above for that.`,
      () => handleInitializeAll(quote),
    );
  };

  const handlePreviewFunding = async () => {
    if (fundThroughSec === null) {
      showNotification('warning', 'Pick a date', 'Choose the date this top-up should cover through.');
      return;
    }
    const horizonLimit = BigInt(Math.floor(Date.now() / 1000)) + admin.maxFundingLookahead;
    if (fundThroughSec > horizonLimit) {
      showNotification(
        'error',
        'Funding date too far ahead',
        `The factory refuses a funding date more than ${
          Number(admin.maxFundingLookahead) / 86400
        } days out. Pick a date on or before ${formatDay(new Date(Number(horizonLimit) * 1000))}.`,
      );
      return;
    }
    try {
      setIsPreviewing(true);
      // The headline number comes from totalFundingRequiredThrough — the same view
      // the runbook quotes — and the plan supplies the per-lock breakdown behind it.
      const [plan, total] = await Promise.all([
        admin.getFundingPlan(fundThroughSec),
        admin.getTotalFundingRequiredThrough(fundThroughSec),
      ]);
      setFundingPlan({ ...plan, total });
    } catch (err: any) {
      showNotification('error', 'Preview failed', err.message || 'Failed to price the funding call');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleFundAllThrough = async () => {
    if (!fundingPlan) return;
    // maxTotal is the previewed total, so a requirement that grew since the quote
    // reverts FundingExceedsMax rather than quietly moving more than was reviewed.
    const hasBalance = await checkFactoryBalance(fundingPlan.total);
    if (!hasBalance) return;
    try {
      await admin.fundAllThrough(fundingPlan.fundThrough, fundingPlan.total);
      showNotification(
        'success',
        'Locks funded',
        `Every lock is funded through ${formatDateTime(
          new Date(Number(fundingPlan.fundThrough) * 1000),
        )}.`,
      );
      setFundingPlan(null);
      setShowFundingBreakdown(false);
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to fund locks');
    }
  };

  const promptFundAllThrough = () => {
    if (!fundingPlan) return;
    const through = formatDateTime(new Date(Number(fundingPlan.fundThrough) * 1000));
    const funded = fundingPlan.entries.filter((e) => e.amount > BigInt(0)).length;
    showConfirm(
      'Fund locks',
      `Push ${web3.formatTokenAmount(fundingPlan.total)} SAHARA from the factory into ${funded} ` +
        `lock${funded === 1 ? '' : 's'}, covering every tranche up to ${through}?\n\n` +
        `The call is capped at this exact total, so nothing larger can be moved by it.`,
      handleFundAllThrough,
    );
  };

  const handleTransferOwnership = async () => {
    try {
      await admin.transferOwnership(newOwnerInput as Address);
      showNotification(
        'success',
        'Transfer started — not complete',
        `${newOwnerInput} is now the pending owner. Ownership does not move until that wallet calls Accept ownership. Until then you are still the owner.`,
      );
      setNewOwnerInput('');
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to start the ownership transfer');
    }
  };

  const promptTransferOwnership = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOwnerInput) {
      showNotification('warning', 'Missing address', 'Enter the wallet that should take over.');
      return;
    }
    showConfirm(
      'Nominate new owner',
      `Nominate ${newOwnerInput} as the next factory owner?\n\nThis does NOT hand over control. ` +
        `That wallet must call Accept ownership itself; until it does, this wallet keeps every ` +
        `owner power and you can cancel the nomination at any time.`,
      handleTransferOwnership,
      true,
    );
  };

  const promptCancelOwnershipTransfer = () =>
    showConfirm(
      'Cancel pending transfer',
      `Clear the pending nomination${admin.pendingOwner ? ` for ${admin.pendingOwner}` : ''}?\n\n` +
        `Ownership stays exactly where it is.`,
      async () => {
        try {
          await admin.cancelOwnershipTransfer();
          showNotification('success', 'Transfer cancelled', 'There is no pending owner any more.');
          await admin.loadAdminData();
        } catch (err: any) {
          showTxErrorToast(err, 'Failed to cancel the transfer');
        }
      },
      true,
    );

  const promptAcceptOwnership = () =>
    showConfirm(
      'Accept ownership',
      `Take ownership of the vesting factory with this wallet?\n\nFrom that moment this wallet ` +
        `owns every lock and is responsible for the monthly top-ups.`,
      async () => {
        try {
          await admin.acceptOwnership();
          showNotification(
            'success',
            'Ownership accepted',
            'This wallet is now the factory owner and the funding obligation moves with it.',
          );
          await admin.loadAdminData();
        } catch (err: any) {
          showTxErrorToast(err, 'Failed to accept ownership');
        }
      },
    );

  /**
   * Move ONE grant to a new wallet.
   *
   * This takes the lock, not the old beneficiary, and that is the whole reason
   * the contract renamed changeBeneficiary(address,address) to
   * reassignLock(address,address): the two have the same selector with different
   * meanings, so a stale form that passed a beneficiary where a lock is now
   * expected would have encoded cleanly and been rejected only by the registry.
   * It has to take the lock in any case — a holder with two grants moving one to
   * a new wallet must be able to say which.
   */
  const handleReassignLock = async () => {
    if (!reassignLockAddress || !replacementBeneficiary) {
      showNotification(
        'warning',
        'Missing fields',
        'Choose the grant to move and the wallet it should move to.',
      );
      return;
    }
    try {
      await admin.reassignLock(reassignLockAddress as Address, replacementBeneficiary as Address);
      showNotification(
        'success',
        'Grant reassigned',
        'The grant now belongs to the new wallet. Its schedule, funding and name are unchanged — only the holder moved.',
      );
      setReassignLockAddress('');
      setReplacementBeneficiary('');
      setShowReassignForm(false);
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to reassign the grant');
    }
  };

  const promptReassignLock = (e: React.FormEvent) => {
    e.preventDefault();
    const details = detailsFor(reassignLockAddress);
    const label = nameOf(reassignLockAddress);
    showConfirm(
      'Reassign grant',
      `Move ${label ? `“${label}”` : 'the grant'} at\n${reassignLockAddress}\n\nfrom:\n${
        details?.beneficiary ?? 'its current holder'
      }\n\nto:\n${replacementBeneficiary}\n\n` +
        `Everything else stays put: the schedule, the tokens already in the lock and anything ` +
        `already claimed. Only the wallet that can claim from here on changes.`,
      handleReassignLock,
      true,
    );
  };

  /**
   * Relabel a grant. The empty string is accepted on chain here and only here,
   * so a label CAN be redacted — but redaction is not removal: the original
   * stays in the calldata, in the LockNameSet event and on every archive node.
   */
  const handleRenameLock = async (lockAddress: string, name: string) => {
    try {
      await admin.setLockName(lockAddress as Address, name);
      showNotification(
        'success',
        name ? 'Grant renamed' : 'Grant name cleared',
        name
          ? `This grant is now labelled “${name}”.`
          : 'The label is now blank. Note that clearing it does not erase it — the previous name stays in this transaction and in the event log forever.',
      );
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to rename the grant');
    }
  };

  const promptRenameLock = (lockAddress: string) => {
    const name = (renameInputs[lockAddress] ?? '').trim();
    const bytes = lockNameByteLength(name);
    if (bytes > MAX_LOCK_NAME_BYTES) {
      showNotification(
        'error',
        'Grant name too long',
        `That name is ${bytes} bytes of UTF-8 and the limit is ${MAX_LOCK_NAME_BYTES}. The contract counts bytes, not characters.`,
      );
      return;
    }
    const current = nameOf(lockAddress);
    if (name === current) {
      toast.info('That is already this grant’s name.');
      return;
    }
    showConfirm(
      name ? 'Rename grant' : 'Clear grant name',
      name
        ? `Relabel ${current ? `“${current}”` : 'this grant'} as “${name}”?\n\nThe label is how operators identify this grant; it changes nothing about the schedule or the tokens.`
        : `Clear the label “${current}”?\n\nRedaction is not removal: the old name stays in the calldata, in the event log and on every archive node.`,
      () => handleRenameLock(lockAddress, name),
      !name,
    );
  };

  /**
   * Resize a grant, at any point in its life and in either direction.
   *
   * The ledger is not touched: a resize moves the SCHEDULE, and because both
   * curves scale linearly in totalAmount the whole entitlement curve rescales
   * with it, the past included. That is what makes a raise owed immediately and
   * a cut pause payment until the smaller curve catches up, so the confirmation
   * below states which of those this particular edit is, in tokens.
   */
  const handleSetLockTotalAmount = async (lockAddress: string, whole: string) => {
    const before = detailsFor(lockAddress);
    try {
      const amountInWei = BigInt(whole) * BigInt(10 ** environment.tokenDecimals);
      const projected = before ? projectResize(before, amountInWei) : null;
      await admin.setLockTotalAmount(lockAddress as Address, amountInWei);
      showNotification(
        'success',
        'Grant amount changed',
        `This grant is now ${web3.formatTokenAmount(amountInWei)} SAHARA. Nothing was migrated: ` +
          `${before ? `${web3.formatTokenAmount(before.unlockedAmount)} SAHARA ` : 'what was '}` +
          `already claimed stands, and the schedule was rescaled around it.` +
          (projected && projected.becomesOwed > BigInt(0)
            ? `\n\n${web3.formatTokenAmount(projected.becomesOwed)} SAHARA is owed as of now. ` +
              `Fund this lock or it reads as Short until you do.`
            : '') +
          (projected && projected.overpaidBy > BigInt(0)
            ? `\n\nThis grant is now overpaid by ${web3.formatTokenAmount(projected.overpaidBy)} ` +
              `SAHARA and will never pay again. Recovering that is a separate ` +
              `emergencyWithdrawFrom transaction.`
            : projected && projected.behindBy > BigInt(0)
              ? `\n\nNothing further is payable until the rescaled curve passes what has already ` +
                `been claimed; it is ${web3.formatTokenAmount(projected.behindBy)} SAHARA behind today.`
              : '') +
          (projected && projected.newlyStranded > BigInt(0)
            ? `\n\n${web3.formatTokenAmount(projected.newlyStranded)} SAHARA is now stranded in the ` +
              `lock above what it can ever pay. Return excess recovers it.`
            : ''),
      );
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to change the grant amount');
    }
  };

  const promptSetLockTotalAmount = (lockAddress: string) => {
    const raw = (amountInputs[lockAddress] ?? '').trim();
    if (!raw || !/^\d+$/.test(raw) || BigInt(raw) === BigInt(0)) {
      showNotification(
        'error',
        'Invalid amount',
        'Enter the new allocation as a whole number of tokens, above zero.',
      );
      return;
    }
    const details = detailsFor(lockAddress);
    const next = BigInt(raw) * BigInt(10 ** environment.tokenDecimals);
    if (details && next === details.totalAmount) {
      toast.info('That is already this grant’s amount.');
      return;
    }
    const label = nameOf(lockAddress);
    const head =
      `Change ${label ? `“${label}”` : 'this grant'} from ${
        details ? web3.formatTokenAmount(details.totalAmount) : '—'
      } to ${web3.formatTokenAmount(next)} SAHARA?\n\n`;

    // Without the lock's own figures nothing can be priced, and the two
    // directions are far too consequential to describe in the abstract.
    if (!details) {
      showConfirm(
        'Change grant amount',
        head +
          `This row's on-chain figures have not loaded, so what this edit does cannot be shown ` +
          `here. A resize rescales the whole entitlement curve, the past included: a raise becomes ` +
          `owed immediately, and a cut can stop payment for years. Expand the row and let it load ` +
          `before signing.`,
        () => handleSetLockTotalAmount(lockAddress, raw),
        true,
      );
      return;
    }

    const p = projectResize(details, next);
    const fmt = (v: bigint) => web3.formatTokenAmount(v);
    const raising = next > details.totalAmount;

    // Both directions reach BACKWARDS, and neither is obvious, so each one is
    // named in this grant's own numbers rather than left to the operator.
    const ledger =
      `A resize moves the schedule, never the ledger: the ${fmt(p.claimed)} SAHARA already ` +
      `claimed stands, and the whole entitlement curve — the past included — rescales around it.\n\n`;

    const body = raising
      ? ledger +
        (p.becomesOwed > BigInt(0)
          ? `OWED IMMEDIATELY. The rescaled curve has released ${fmt(p.entitledAfter)} SAHARA as of ` +
            `today rather than ${fmt(p.entitledNow)}, and ${fmt(p.claimed)} of that has been paid, ` +
            `so ${fmt(p.becomesOwed)} SAHARA falls due the moment this is mined` +
            (p.owedNow > BigInt(0) ? `, on top of the ${fmt(p.owedNow)} already owed` : '') +
            `. It is claimable as soon as the lock is funded for it, and until then this grant ` +
            `reads as Short ${fmt(p.owedAfter)}.`
          : p.behindBy > BigInt(0)
            ? `Nothing falls due today even so: the rescaled curve is still ${fmt(p.behindBy)} ` +
              `SAHARA behind the ${fmt(p.claimed)} already claimed, and payment resumes only once ` +
              `it passes that.`
            : `Nothing has been released on this schedule yet, so nothing falls due today — the ` +
              `curve simply scales up from here.`)
      : ledger +
        (p.overpaidBy > BigInt(0)
          ? `THIS CUTS BELOW WHAT HAS ALREADY BEEN PAID. ${fmt(p.claimed)} SAHARA has been claimed ` +
            (p.ceilingAfter === next
              ? `against a corrected grant of ${fmt(next)}. `
              : `against a grant this takes to ${fmt(next)} — and this one is frozen, so its ` +
                `off-board ceiling rescales with the amount and the most it could ever pay drops ` +
                `to ${fmt(p.ceilingAfter)}. `) +
            `The beneficiary keeps every token of it — ` +
            `tokens already paid cannot be recalled by arithmetic — and this grant pays them ` +
            `NOTHING FURTHER, ever, however long the schedule runs.\n\n` +
            `It will read as overpaid by ${fmt(p.overpaidBy)} SAHARA. Getting that back is not part ` +
            `of this transaction: it is emergencyWithdrawFrom, separately and loudly.`
          : p.owedAfter === BigInt(0) && p.behindBy > BigInt(0)
            ? `PAYMENT PAUSES. Lowering rescales the past too: the smaller curve says only ` +
              `${fmt(p.entitledAfter)} SAHARA should have been released by now, and ` +
              `${fmt(p.claimed)} has already been paid. This grant therefore pays nothing further ` +
              `until the smaller curve climbs past what has already been taken — it is ` +
              `${fmt(p.behindBy)} SAHARA behind that today, and the wait runs in years, not months.` +
              (p.owedNow > BigInt(0)
                ? ` The ${fmt(p.owedNow)} SAHARA owed right now stops being owed.`
                : '') +
              `\n\nNothing is forfeited: payment resumes by itself once the curve passes ` +
              `${fmt(p.claimed)}.`
            : `Lowering rescales the past too: as of today the curve has released ` +
              `${fmt(p.entitledAfter)} SAHARA rather than ${fmt(p.entitledNow)}. ` +
              `${fmt(p.claimed)} has been paid, so ${fmt(p.owedAfter)} stays owed right now, down ` +
              `from ${fmt(p.owedNow)}, and every later tranche shrinks in the same proportion.`) +
        (p.newlyStranded > BigInt(0)
          ? `\n\n${fmt(p.newlyStranded)} SAHARA already sitting in the lock drops above what it can ` +
            `ever pay. It is not lost — Return excess, in this same row, recovers it.`
          : '');

    showConfirm(
      raising ? 'Raise grant amount' : 'Lower grant amount',
      head + body,
      () => handleSetLockTotalAmount(lockAddress, raw),
      // Destructive styling for the one edit that takes the grant below what has
      // already been paid out, matching the cancellation dialog.
      p.overpaidBy > BigInt(0),
    );
  };

  /**
   * Cancel a grant and sweep its balance, in one transaction.
   *
   * FULL CLAWBACK. The beneficiary forfeits everything they have not already
   * claimed, INCLUDING tokens they had vested and earned but never collected.
   * The confirmation names all three irreversible facts because nothing else on
   * this page reaches this far.
   */
  const handleCancelLock = async (lockAddress: string, recipient: string) => {
    try {
      const result = await admin.cancelLock(lockAddress as Address, recipient as Address);
      showNotification(
        'success',
        'Grant cancelled',
        result
          ? `${web3.formatTokenAmount(result.forfeited)} SAHARA forfeited by the beneficiary; ` +
            `${web3.formatTokenAmount(result.swept)} SAHARA swept to ${recipient}. ` +
            `The two differ because a lock only ever holds what has been wired to it.\n\n` +
            `The grant stays in the roster and the lock stays at its address; it simply pays ` +
            `nothing, ever again.`
          : `The grant is cancelled and its balance was swept to ${recipient}.`,
      );
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to cancel the grant');
    }
  };

  const promptCancelLock = (lockAddress: string) => {
    const recipient = (recipientInputs[lockAddress] ?? web3.address ?? '').trim();
    if (!recipient) {
      showNotification(
        'error',
        'No recipient',
        'Enter the wallet the swept tokens should go to. The sweep happens in the same transaction as the cancellation, so it cannot be left blank.',
      );
      return;
    }
    const details = detailsFor(lockAddress);
    const label = nameOf(lockAddress);
    const ceiling = details ? ceilingOf(details) : BigInt(0);
    const unclaimed = details && ceiling > details.unlockedAmount
      ? ceiling - details.unlockedAmount
      : BigInt(0);
    showConfirm(
      'Cancel grant — full clawback',
      `Cancel ${label ? `“${label}”` : 'the grant'} at ${lockAddress}` +
        (details ? `, held by ${details.beneficiary}` : '') +
        `?\n\n` +
        `1. This is PERMANENT. A cancelled grant can never be reinstated, and off-boarding, ` +
        `reactivating, changing the amount and reassigning are all refused on it afterwards. The ` +
        `only remedy for a mistake is to issue a replacement grant.\n\n` +
        `2. EVERYTHING the beneficiary has not already claimed is forfeit — including ` +
        (details ? `${web3.formatTokenAmount(unclaimed)} SAHARA ` : 'tokens ') +
        `they had already vested and earned but never collected` +
        (details && details.earnedUnclaimed > BigInt(0)
          ? `, of which ${web3.formatTokenAmount(details.earnedUnclaimed)} SAHARA is already earned` +
            (details.availableAmount < details.earnedUnclaimed
              ? ` (only ${web3.formatTokenAmount(details.availableAmount)} of it funded, so that is all they could collect before you sign this)`
              : ' and collectable right now')
          : '') +
        `. Only what they have already taken is out of reach.\n\n` +
        `3. The lock's ENTIRE BALANCE` +
        (details ? ` of ${web3.formatTokenAmount(details.lockBalance)} SAHARA` : '') +
        ` is swept to ${recipient} in this same transaction.`,
      () => handleCancelLock(lockAddress, recipient),
      true,
    );
  };

  /**
   * Recover tokens stranded above what a lock can ever pay. Funding only ever
   * pushes, so a ceiling that drops after a top-up leaves a residue nothing else
   * can reach. The contract reverts on zero rather than no-opping, so the button
   * only appears when there is something to recover.
   */
  const handleReturnLockExcess = async (lockAddress: string, recipient: string) => {
    try {
      const amount = await admin.returnLockExcess(lockAddress as Address, recipient as Address);
      showNotification(
        'success',
        'Excess recovered',
        `${amount !== null ? web3.formatTokenAmount(amount) : 'The stranded'} SAHARA returned to ${recipient}. Nothing the beneficiary can still claim was touched.`,
      );
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to return the excess');
    }
  };

  const promptReturnLockExcess = (lockAddress: string) => {
    const recipient = (recipientInputs[lockAddress] ?? web3.address ?? '').trim();
    if (!recipient) {
      showNotification('error', 'No recipient', 'Enter the wallet the recovered tokens should go to.');
      return;
    }
    const details = detailsFor(lockAddress);
    showConfirm(
      'Return excess funding',
      `Return ${details ? web3.formatTokenAmount(details.excessFunding) : 'the stranded'} SAHARA ` +
        `from ${lockAddress} to ${recipient}?\n\n` +
        `This only moves tokens the lock can never pay out. Everything still owed to the ` +
        `beneficiary stays where it is.`,
      () => handleReturnLockExcess(lockAddress, recipient),
      true,
    );
  };

  const handlePauseLock = async (lockAddress: string) => {
    try {
      await admin.pauseLock(lockAddress as Address);
      showNotification('success', 'Lock paused', 'The beneficiary cannot claim until unpaused.');
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to pause lock');
    }
  };

  const promptPauseLock = (lockAddress: string) =>
    showConfirm(
      'Pause lock',
      `Pause lock at ${lockAddress}?\n\nClaims will be blocked until the lock is unpaused.`,
      () => handlePauseLock(lockAddress),
      true,
    );

  const handleUnpauseLock = async (lockAddress: string) => {
    try {
      await admin.unpauseLock(lockAddress as Address);
      showNotification('success', 'Lock unpaused', 'Claims are open again.');
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to unpause lock');
    }
  };

  const promptUnpauseLock = (lockAddress: string) =>
    showConfirm(
      'Unpause lock',
      `Unpause lock at ${lockAddress}?\n\nThe beneficiary will be able to claim again.`,
      () => handleUnpauseLock(lockAddress),
    );

  const handleSetOffboardTime = async (lockAddress: string, offboardSec: bigint) => {
    try {
      await admin.setLockOffboardTime(lockAddress as Address, offboardSec);
      if (offboardSec === BigInt(0)) {
        showNotification(
          'success',
          'Off-board cleared',
          'Vesting has resumed under the original schedule.',
        );
      } else {
        showNotification(
          'success',
          'Off-boarded',
          'Vesting is frozen as of the selected date. Already-vested tokens remain claimable.',
        );
      }
      await admin.loadAdminData();
    } catch (err: any) {
      showTxErrorToast(err, 'Failed to update off-board time');
    }
  };

  const promptOffboard = (lockAddress: string) => {
    const input = offboardInputs[lockAddress];
    if (!input) {
      showNotification('warning', 'Pick a date', 'Choose the off-board date and time first.');
      return;
    }
    const ms = new Date(input).getTime();
    if (Number.isNaN(ms)) {
      showNotification('error', 'Invalid date', 'The off-board date could not be parsed.');
      return;
    }
    const offboardSec = BigInt(Math.floor(ms / 1000));
    showConfirm(
      'Off-board beneficiary',
      `Freeze vesting for ${lockAddress} as of ${new Date(ms).toLocaleString()}?\n\nAlready-vested tokens stay claimable. Unvested tokens remain in the lock contract.`,
      () => handleSetOffboardTime(lockAddress, offboardSec),
      true,
    );
  };

  const promptReactivate = (lockAddress: string) => {
    showConfirm(
      'Clear off-board',
      `Reactivate vesting for ${lockAddress}?\n\nThe original schedule resumes from now.`,
      () => handleSetOffboardTime(lockAddress, BigInt(0)),
    );
  };

  const promptSetUnlockCliff = (category: Category) => {
    const label = categoryLabel(category);
    const input = cliffInputs[category];
    if (!input) {
      showNotification('warning', 'Pick a date', `Choose the ${label} unlock cliff date first.`);
      return;
    }
    const ms = new Date(input).getTime();
    if (Number.isNaN(ms)) {
      showNotification('error', 'Invalid date', 'The unlock cliff date could not be parsed.');
      return;
    }
    const sec = BigInt(Math.floor(ms / 1000));
    // Spell out a backdated cliff before signing: it releases immediately, and the
    // date cannot be corrected afterwards.
    const isPast = ms <= Date.now();
    showConfirm(
      `Set ${label} unlock cliff`,
      `Pin the ${label} unlock cliff to ${new Date(ms).toLocaleString()}?\n\n` +
        `This moves every ${label} lock at once and cannot be undone or changed later.` +
        (isPast
          ? `\n\nThis date is in the past: 25% becomes claimable as soon as the transaction lands, plus every monthly tranche already elapsed.`
          : ''),
      async () => {
        try {
          await admin.setUnlockCliff(category, sec);
          showNotification(
            'success',
            'Unlock cliff set',
            `${label} locks release 25% on ${formatDateTime(new Date(ms))}, then one of 36 tranches every 30 days.`,
          );
        } catch (err: any) {
          showTxErrorToast(err, 'Failed to set unlock cliff');
        }
      },
      true,
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        destructive={confirmModal.destructive}
        onConfirm={() => {
          confirmModal.onConfirm();
          closeConfirm();
        }}
        onCancel={closeConfirm}
      />

      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={closeNotification}
      />

      {admin.error && (
        <div className="sh-banner sh-banner-danger">
          <Icon d={ICONS.warn} size={16} stroke={1.6} />
          <div style={{ flex: 1 }}>{admin.error}</div>
          <button
            type="button"
            onClick={() => admin.setError(null)}
            className="sh-btn sh-btn-ghost sh-btn-sm"
            aria-label="Dismiss"
            style={{ padding: 6 }}
          >
            <Icon d={ICONS.close} size={14} />
          </button>
        </div>
      )}

      {/* The panel's write surface is the current factory ABI. When the app is
          pointed at an older deployment the reads still decode — that is what
          factoryAbiVersion is for — but named grants, reassignment, amount
          correction and cancellation do not exist there, so say so once rather
          than letting each control revert with a bare selector. */}
      {environment.factoryAbiVersion !== 'v10' && (
        <div className="sh-banner sh-banner-warn">
          <Icon d={ICONS.info} size={16} stroke={1.6} />
          <div>
            <div style={{ fontWeight: 500, color: 'var(--text)' }}>
              This factory predates named multi-grant distributions
            </div>
            <div style={{ marginTop: 2 }}>
              <span className="sh-mono">{environment.factoryAddress}</span> is a{' '}
              {environment.factoryAbiVersion} deployment: one grant per wallet, no names, no
              cancellation. Everything on this page READS correctly, but creating a grant,
              reassigning one, correcting an amount, renaming, cancelling — and now arming and
              funding, whose two catch-up calls take a spend cap only on the newer factory — all
              target the newer factory and will revert here. Grant names show as blank for the
              same reason.
            </div>
          </div>
        </div>
      )}

      {isConnectedPendingOwner && !isConnectedOwner && (
        <div className="sh-banner sh-banner-warn">
          <Icon d={ICONS.info} size={16} stroke={1.6} />
          <div>
            <div style={{ fontWeight: 500, color: 'var(--text)' }}>
              You are the pending owner, not the owner
            </div>
            <div style={{ marginTop: 2 }}>
              Nothing has been handed over yet. Every action on this page except Accept ownership
              still needs the current owner's key and will revert from this wallet.
            </div>
          </div>
        </div>
      )}

      {/* Factory Control */}
      <div className="sh-card">
        <div className="sh-card-head">
          <div>
            <div className="sh-label">Factory control</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>Vesting factory</div>
          </div>
          <span className={`sh-pill ${isFactoryPaused ? 'sh-pill-danger' : 'sh-pill-success'}`}>
            {isFactoryPaused ? 'Paused' : 'Active'}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="sh-label">Address</span>
            <span className="sh-mono" style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {environment.factoryAddress}
            </span>
            <button type="button" onClick={handleCopyFactoryAddress} className="sh-btn sh-btn-sm">
              <Icon d={ICONS.copy} size={12} />
              Copy
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={handlePauseFactory}
              disabled={admin.isLoading || isFactoryPaused}
              className="sh-btn sh-btn-danger"
            >
              Pause factory
            </button>
            <button
              type="button"
              onClick={handleUnpauseFactory}
              disabled={admin.isLoading || !isFactoryPaused}
              className="sh-btn sh-btn-success"
            >
              Unpause factory
            </button>
          </div>
        </div>
      </div>

      {/* Unlock cliffs — the dates that actually gate claiming. One-way per track,
          same reasoning as TGE: a beneficiary who can see their date must never have
          it move under them. Read from chain, never hardcoded. */}
      <div className="sh-card">
        <div className="sh-card-head">
          <div>
            <div className="sh-label">Unlock schedule</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>Unlock cliff dates</div>
          </div>
        </div>
        <div className="sh-cause">
          Each track releases nothing until its cliff date, 25% on the date itself, then one
          of 36 tranches every 30 days. While a track is unset, its locks cannot be claimed
          at all.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          {[Category.INVESTOR, Category.TEAM].map((category, idx) => {
            const label = categoryLabel(category);
            const isSet = admin.unlockCliffTimes[category] > BigInt(0);
            return (
              <div
                key={category}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  paddingTop: idx > 0 ? 16 : 0,
                  borderTop: idx > 0 ? '1px solid var(--border)' : undefined,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <span className="sh-label">{label} track</span>
                  <span className={`sh-pill ${isSet ? 'sh-pill-success' : 'sh-pill-warn'}`}>
                    {cliffLabel(category)}
                  </span>
                </div>
                {isSet ? (
                  <div className="sh-cause">
                    Locked in for every {label} lock and cannot be changed.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 10,
                        alignItems: 'center',
                      }}
                    >
                      <input
                        type="datetime-local"
                        value={cliffInputs[category] ?? ''}
                        onChange={(e) =>
                          setCliffInputs((prev) => ({ ...prev, [category]: e.target.value }))
                        }
                        className="sh-input"
                      />
                      <button
                        type="button"
                        onClick={() => promptSetUnlockCliff(category)}
                        disabled={admin.isLoading}
                        className="sh-btn sh-btn-primary"
                      >
                        Set {label.toLowerCase()} cliff
                      </button>
                    </div>
                    <div className="sh-cause">
                      One-way: this moves every {label} lock at once and can never be
                      corrected. A date in the past makes that 25% claimable the moment the
                      transaction lands. The other track is set separately.
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* TGE — still one-way, but now a recorded date only: no schedule reads it,
          the cliff dates above are what gate claiming. */}
      <div className="sh-card">
        <div className="sh-card-head">
          <div>
            <div className="sh-label">TGE</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>Token generation event</div>
          </div>
          <span className={`sh-pill ${admin.tgeTime > BigInt(0) ? 'sh-pill-success' : 'sh-pill-warn'}`}>
            {admin.tgeTime > BigInt(0) ? formatDateTime(new Date(Number(admin.tgeTime) * 1000)) : 'Unset'}
          </span>
        </div>
        {admin.tgeTime > BigInt(0) ? (
          <div className="sh-cause">
            Recorded on chain and cannot be changed. It is a reference date only — no lock
            counts its unlock or vesting schedule from it.
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
              <input
                type="datetime-local"
                value={tgeInput}
                onChange={(e) => setTgeInput(e.target.value)}
                className="sh-input"
              />
              <button
                type="button"
                onClick={() => {
                  const ms = new Date(tgeInput).getTime();
                  if (Number.isNaN(ms)) {
                    showNotification('error', 'Invalid date', 'Pick a valid TGE date.');
                    return;
                  }
                  const sec = BigInt(Math.floor(ms / 1000));
                  showConfirm(
                    'Record TGE',
                    `Record the TGE as ${new Date(ms).toLocaleString()}? This is a reference date only — it releases nothing and moves no unlock schedule. It cannot be changed once set.`,
                    async () => {
                      try {
                        await admin.setTge(sec);
                        showNotification(
                          'success',
                          'TGE recorded',
                          'The date is on chain for reference. Unlocking is still gated by the per-track cliff dates.',
                        );
                      } catch (err: any) {
                        showTxErrorToast(err, 'Failed to set TGE');
                      }
                    },
                    true,
                  );
                }}
                className="sh-btn sh-btn-primary"
              >
                Record TGE
              </button>
            </div>
            <div className="sh-cause" style={{ marginTop: 10 }}>
              Recording the TGE does not unlock anything — claims are gated by the cliff
              dates above. Settable once.
            </div>
          </>
        )}
      </div>

      {/* Create Lock */}
      <div className="sh-card">
        <div className="sh-card-head">
          <div>
            <div className="sh-label">Create lock</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>New allocation</div>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateForm((v) => !v)}
            className={`sh-btn ${showCreateForm ? '' : 'sh-btn-primary'}`}
          >
            {showCreateForm ? 'Cancel' : (<><Icon d={ICONS.plus} size={14} />New lock</>)}
          </button>
        </div>

        {showCreateForm && (
          <form
            onSubmit={handleCreateLock}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
          >
            <div className="sh-field" style={{ gridColumn: '1 / -1' }}>
              <label>Grant name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="SEED-001"
                className="sh-input"
                required
              />
              <div
                className="sh-cause"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  color: newNameBytes > MAX_LOCK_NAME_BYTES ? 'var(--danger)' : undefined,
                }}
              >
                <span>
                  Required. One wallet may hold several grants, so this is what says which one
                  this is — it leads every row and every funding breakdown below.
                </span>
                {/* BYTES, not characters. The contract bounds bytes of UTF-8 and
                    the two differ by a factor of three for CJK text, so a counter
                    reading name.length would pass a label createLock rejects. */}
                <span className="sh-mono" style={{ whiteSpace: 'nowrap' }}>
                  {newNameBytes} / {MAX_LOCK_NAME_BYTES} bytes
                </span>
              </div>
            </div>
            <div className="sh-field" style={{ gridColumn: '1 / -1' }}>
              <label>Beneficiary address</label>
              <input
                type="text"
                value={newBeneficiary}
                onChange={(e) => setNewBeneficiary(e.target.value)}
                placeholder="0x…"
                className="sh-input"
                required
              />
              <div className="sh-cause">
                An address may hold several grants, each with its own name and schedule.
                You can also change an existing grant’s amount after it has begun.
              </div>
            </div>
            <div className="sh-field">
              <label>Total amount (tokens)</label>
              <input
                type="number"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                placeholder="1000000"
                className="sh-input"
                required
                min={0}
              />
            </div>
            <div className="sh-field">
              <label>Start date &amp; time</label>
              <input
                type="datetime-local"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                className="sh-input"
                required
              />
              <div className="sh-cause">
                Your local time. Earliest start: January 1, 2022 (UTC). No more than 365 days after
                the current chain time. The start date cannot be changed after creation.
              </div>
            </div>
            <div className="sh-field" style={{ gridColumn: '1 / -1' }}>
              <label>Unlock track</label>
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(Number(e.target.value) as Category)}
                className="sh-select"
              >
                <option value={Category.INVESTOR}>
                  {`Investor — unlock cliff ${cliffLabel(Category.INVESTOR, 'not scheduled yet')}`}
                </option>
                <option value={Category.TEAM}>
                  {`Team — unlock cliff ${cliffLabel(Category.TEAM, 'not scheduled yet')}`}
                </option>
              </select>
              <div className="sh-cause">
                The track is fixed when the lock is deployed. Putting a beneficiary on the
                wrong one can only be undone by replacing the lock.
              </div>
            </div>
            <button
              type="submit"
              disabled={admin.isLoading || newNameBytes === 0 || newNameBytes > MAX_LOCK_NAME_BYTES}
              className="sh-btn sh-btn-primary sh-btn-block"
              style={{ gridColumn: '1 / -1' }}
              title={
                newNameBytes === 0
                  ? 'A grant name is required'
                  : newNameBytes > MAX_LOCK_NAME_BYTES
                    ? `The name is ${newNameBytes} bytes of UTF-8; the limit is ${MAX_LOCK_NAME_BYTES}`
                    : undefined
              }
            >
              {admin.isLoading ? 'Creating…' : 'Create grant'}
              <Icon d={ICONS.arrowRight} size={14} />
            </button>
          </form>
        )}
      </div>

      {/* Funding — the recurring job. Locks are not pre-loaded with the whole
          allocation any more: the treasury wires roughly one period to the factory
          and this pushes exactly the shortfall against the schedule. */}
      {admin.incrementalFunding === true && <div className="sh-card">
        <div className="sh-card-head">
          <div>
            <div className="sh-label">Funding</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>Top up locks</div>
          </div>
          <span
            className={`sh-pill ${
              admin.unfundedEarned > BigInt(0) || fundingDue.kind === 'now'
                ? 'sh-pill-danger'
                : 'sh-pill-success'
            }`}
          >
            {admin.unfundedEarned > BigInt(0) || fundingDue.kind === 'now'
              ? 'Top-up due now'
              : fundingDue.kind === 'future'
                ? `Next due ${formatDay(fundingDue.date)}`
                : 'Nothing due'}
          </span>
        </div>

        {admin.unfundedEarned > BigInt(0) && (
          <div className="sh-banner sh-banner-danger" style={{ marginBottom: 16 }}>
            <Icon d={ICONS.warn} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                {web3.formatTokenAmount(admin.unfundedEarned)} SAHARA is earned and unpayable
              </div>
              <div style={{ marginTop: 2 }}>
                At least one beneficiary has tranches they cannot claim because the tokens are not
                in their lock. A top-up is already overdue.
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <div className="sh-label">Next top-up due</div>
            <div
              style={{
                fontSize: 15,
                marginTop: 8,
                color: fundingDue.kind === 'now' ? 'var(--danger)' : undefined,
                fontWeight: fundingDue.kind === 'now' ? 500 : undefined,
              }}
            >
              {fundingDue.kind === 'now'
                ? 'NOW'
                : fundingDue.kind === 'future'
                  ? formatDateTime(fundingDue.date)
                  : '—'}
            </div>
            <div className="sh-cause" style={{ marginTop: 6 }}>
              {fundingDue.kind === 'now'
                ? 'Someone is already short — fund through now'
                : fundingDue.kind === 'future'
                  ? `Earliest tranche still to land across ${admin.allLocks.length} lock${
                      admin.allLocks.length === 1 ? '' : 's'
                    }`
                  : 'Nothing owed and nothing further will ever release'}
            </div>
          </div>
          <div>
            <div className="sh-label">Earned, unfunded</div>
            <div
              className="sh-num"
              style={{
                fontSize: 20,
                marginTop: 8,
                color: admin.unfundedEarned > BigInt(0) ? 'var(--danger)' : 'var(--text)',
              }}
            >
              {web3.formatTokenAmount(admin.unfundedEarned)}
            </div>
            <div className="sh-cause" style={{ marginTop: 6 }}>
              Owed to beneficiaries right now
            </div>
          </div>
          <div>
            <div className="sh-label">Factory balance</div>
            <div className="sh-num" style={{ fontSize: 20, marginTop: 8 }}>
              {web3.formatTokenAmount(admin.factoryBalance)}
            </div>
            <div className="sh-cause" style={{ marginTop: 6 }}>
              On hand to push into locks
            </div>
          </div>
          <div>
            <div className="sh-label">Remaining obligation</div>
            <div className="sh-num" style={{ fontSize: 20, marginTop: 8 }}>
              {web3.formatTokenAmount(admin.remainingObligation)}
            </div>
            <div className="sh-cause" style={{ marginTop: 6 }}>
              Lifetime tokens still to wire, ignoring any date. An off-boarded lock
              counts only up to what it froze at, and a cancelled one counts nothing.
            </div>
          </div>
          <div>
            <div className="sh-label">Stranded excess</div>
            <div
              className="sh-num"
              style={{
                fontSize: 20,
                marginTop: 8,
                color: admin.totalExcessFunding > BigInt(0) ? 'var(--warn)' : 'var(--text)',
              }}
            >
              {web3.formatTokenAmount(admin.totalExcessFunding)}
            </div>
            <div className="sh-cause" style={{ marginTop: 6 }}>
              The mirror of "earned, unfunded": tokens sitting in locks above anything those
              locks can ever pay. Recover each one with Return excess.
            </div>
          </div>
        </div>

        <div className="sh-field" style={{ marginBottom: 12 }}>
          <label>Fund through</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10 }}>
            <input
              type="datetime-local"
              aria-label="Fund through"
              step={1}
              value={fundThroughInput}
              onChange={(e) => {
                setFundThroughInput(e.target.value);
                setFundingPlan(null);
                setShowFundingBreakdown(false);
              }}
              className="sh-input"
            />
            <button
              type="button"
              onClick={handlePreviewFunding}
              disabled={admin.isLoading || isPreviewing || admin.allLocks.length === 0}
              className="sh-btn"
            >
              {isPreviewing ? 'Pricing…' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={promptFundAllThrough}
              disabled={
                admin.isLoading ||
                !previewMatchesDate ||
                !fundingPlan ||
                fundingPlan.total === BigInt(0)
              }
              className="sh-btn sh-btn-primary"
              title={
                !previewMatchesDate
                  ? 'Preview the funding date first'
                  : fundingPlan && fundingPlan.total === BigInt(0)
                    ? 'Every lock is already funded through this date'
                    : 'Push exactly the previewed total'
              }
            >
              {admin.isLoading ? 'Funding…' : 'Fund locks'}
              <Icon d={ICONS.arrowRight} size={14} />
            </button>
          </div>
        </div>

        {fundingPlan && previewMatchesDate ? (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div className="sh-label">To wire for this top-up</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
                  <span
                    className="sh-num"
                    style={{ fontSize: 30, fontWeight: 400, letterSpacing: '-0.03em' }}
                  >
                    {web3.formatTokenAmount(fundingPlan.total)}
                  </span>
                  <span className="sh-mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    SAHARA
                  </span>
                </div>
                <div className="sh-cause" style={{ marginTop: 8 }}>
                  Covers every tranche up to{' '}
                  {formatDateTime(new Date(Number(fundingPlan.fundThrough) * 1000))} ·{' '}
                  {fundingPlan.entries.filter((e) => e.amount > BigInt(0)).length} of{' '}
                  {fundingPlan.entries.length} lock
                  {fundingPlan.entries.length === 1 ? '' : 's'} need tokens
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowFundingBreakdown((v) => !v)}
                className="sh-btn sh-btn-sm"
              >
                {showFundingBreakdown ? 'Hide breakdown' : 'Per-lock breakdown'}
                <Icon d={showFundingBreakdown ? ICONS.caret : ICONS.chevron} size={12} />
              </button>
            </div>

            {fundingPlan.total > admin.factoryBalance && (
              <div className="sh-banner sh-banner-warn">
                <Icon d={ICONS.warn} size={16} stroke={1.6} />
                <div>
                  The factory is {web3.formatTokenAmount(fundingPlan.total - admin.factoryBalance)}{' '}
                  SAHARA short of this total. Wire the difference from the treasury before signing —
                  the call reverts otherwise.
                </div>
              </div>
            )}

            {showFundingBreakdown && (
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                <table className="sh-table">
                  <thead>
                    <tr>
                      {/* The name leads, because this table is what an operator
                          reads immediately before signing a transfer. A row of
                          hex addresses cannot be checked against anything; a row
                          of grant names can be checked against the roster. */}
                      <th>Name</th>
                      <th>Lock</th>
                      <th>Beneficiary</th>
                      <th className="num">To send</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fundingPlan.entries.map((entry) => (
                      <tr key={entry.lock}>
                        <td style={{ color: nameOf(entry.lock) ? undefined : 'var(--text-muted)' }}>
                          {detailsFor(entry.lock) ? nameOf(entry.lock) || 'Unnamed' : 'Not loaded'}
                          {detailsFor(entry.lock)?.cancelled && (
                            <span className="sh-pill sh-pill-danger" style={{ marginLeft: 8 }}>
                              Cancelled
                            </span>
                          )}
                        </td>
                        <td className="sh-mono" style={{ color: 'var(--text-2)' }}>
                          {web3.formatAddress(entry.lock)}
                        </td>
                        <td className="sh-mono" style={{ color: 'var(--text-2)' }}>
                          {detailsFor(entry.lock)
                            ? web3.formatAddress(detailsFor(entry.lock)!.beneficiary)
                            : '—'}
                        </td>
                        <td
                          className="num sh-num"
                          style={{ color: entry.amount > BigInt(0) ? 'var(--text)' : 'var(--text-muted)' }}
                        >
                          {web3.formatTokenAmount(entry.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="sh-cause">
            Pick the date this top-up should carry every beneficiary through — usually the next
            tranche date above — then preview it. The contract prices each lock against its own
            schedule and sends only the shortfall, and the call is capped at the previewed total.
            Dates further than {Number(admin.maxFundingLookahead) / 86400} days out are rejected on
            chain.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid var(--border)',
          }}
        >
          <div className="sh-cause" style={{ maxWidth: 560 }}>
            Catching up to today arms any un-armed lock and pays off whatever is already earned.
            It is repeatable and moves nothing when no one is short.
          </div>
          <button
            type="button"
            onClick={promptInitializeAll}
            disabled={admin.isLoading || admin.allLocks.length === 0}
            className="sh-btn"
          >
            {admin.isLoading ? 'Working…' : 'Catch up to today'}
          </button>
        </div>
      </div>}

      {/* Ownership — two-step. transferOwnership only nominates; the factory does
          not change hands until the nominee calls acceptOwnership. */}
      {admin.incrementalFunding === true && <div className="sh-card">
        <div className="sh-card-head">
          <div>
            <div className="sh-label">Ownership</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>Factory owner</div>
          </div>
          <span className={`sh-pill ${admin.pendingOwner ? 'sh-pill-warn' : 'sh-pill-success'}`}>
            {admin.pendingOwner ? 'Transfer pending' : 'Settled'}
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
          }}
        >
          <div>
            <div className="sh-label">Current owner</div>
            <div className="sh-mono" style={{ fontSize: 12, marginTop: 8, wordBreak: 'break-all' }}>
              {admin.factoryOwner ?? '—'}
            </div>
            {isConnectedOwner && <div className="sh-cause" style={{ marginTop: 6 }}>This wallet</div>}
          </div>
          <div>
            <div className="sh-label">Pending owner</div>
            <div
              className="sh-mono"
              style={{
                fontSize: 12,
                marginTop: 8,
                wordBreak: 'break-all',
                color: admin.pendingOwner ? 'var(--accent)' : 'var(--text-muted)',
              }}
            >
              {admin.pendingOwner ?? 'None'}
            </div>
            {isConnectedPendingOwner && (
              <div className="sh-cause" style={{ marginTop: 6 }}>This wallet</div>
            )}
          </div>
        </div>

        {admin.pendingOwner && (
          <div className="sh-banner sh-banner-warn" style={{ marginTop: 16 }}>
            <Icon d={ICONS.warn} size={16} stroke={1.6} />
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                This transfer is not complete
              </div>
              <div style={{ marginTop: 2 }}>
                <span className="sh-mono">{admin.pendingOwner}</span> has been nominated but owns
                nothing yet. Until that wallet signs Accept ownership,{' '}
                <span className="sh-mono">{admin.factoryOwner ?? 'the current owner'}</span> keeps
                every owner power — including the monthly funding calls — and the nomination can
                still be cancelled.
              </div>
            </div>
          </div>
        )}

        {isConnectedPendingOwner && (
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              onClick={promptAcceptOwnership}
              disabled={admin.isLoading}
              className="sh-btn sh-btn-primary"
            >
              Accept ownership
              <Icon d={ICONS.arrowRight} size={14} />
            </button>
            <div className="sh-cause" style={{ marginTop: 8 }}>
              You are the nominee. Nothing has moved yet — this signature is what completes the
              handover, and it also makes this wallet responsible for funding every lock.
            </div>
          </div>
        )}

        <form
          onSubmit={promptTransferOwnership}
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div className="sh-field">
            <label>Transfer ownership to</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10 }}>
              <input
                type="text"
                value={newOwnerInput}
                onChange={(e) => setNewOwnerInput(e.target.value)}
                placeholder="0x…"
                className="sh-input"
              />
              <button
                type="submit"
                disabled={admin.isLoading || !newOwnerInput}
                className="sh-btn sh-btn-primary"
              >
                Nominate
              </button>
              <button
                type="button"
                onClick={promptCancelOwnershipTransfer}
                disabled={admin.isLoading || !admin.pendingOwner}
                className="sh-btn sh-btn-danger"
                title={
                  admin.pendingOwner
                    ? 'Clear the pending nomination; ownership stays put'
                    : 'There is no pending transfer to cancel'
                }
              >
                Cancel transfer
              </button>
            </div>
          </div>
          <div className="sh-cause">
            Nominating does not hand over control: it only sets the pending owner. The factory keeps
            its current owner until the nominee calls Accept ownership, so a typo here is
            recoverable — cancel it. Ownership can never be renounced; funding is a standing
            obligation.
          </div>
        </form>
      </div>}

      {admin.incrementalFunding === false && (
        <div className="sh-card">
          <div className="sh-h2">Initialize allocations</div>
          <p className="sh-cause" style={{ marginTop: 12 }}>
            This factory funds each allocation in full at initialization. Periodic top-ups
            and two-step ownership require a new factory deployment.
          </p>
          <p className="sh-cause" style={{ marginTop: 12 }}>
            Required: {web3.formatTokenAmount(admin.remainingObligation)} SAHARA · Factory balance:{' '}
            {web3.formatTokenAmount(admin.factoryBalance)} SAHARA
          </p>
          <button type="button" className="sh-btn sh-btn-primary" style={{ marginTop: 16 }}
            onClick={promptInitializeAll} disabled={admin.isLoading || admin.remainingObligation === 0n}>
            Initialize all locks
          </button>
        </div>
      )}

      {/* Reassign a grant. This replaces the old change-beneficiary form, and it
          names the GRANT rather than its holder: one wallet may hold several, so
          a holder is no longer a unique reference to anything. */}
      <div className="sh-card">
        <div className="sh-card-head">
          <div>
            <div className="sh-label">Rotation</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>Reassign a grant</div>
          </div>
          <button
            type="button"
            onClick={() => setShowReassignForm((v) => !v)}
            className={`sh-btn ${showReassignForm ? '' : 'sh-btn-primary'}`}
          >
            {showReassignForm ? 'Cancel' : 'Reassign'}
          </button>
        </div>

        {showReassignForm && (
          <form
            onSubmit={promptReassignLock}
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
          >
            <div className="sh-field">
              <label>Grant to move (current page)</label>
              <select
                value={reassignLockAddress}
                onChange={(e) => setReassignLockAddress(e.target.value)}
                className="sh-select"
                required
              >
                <option value="">Select a grant…</option>
                {groupedLocks.map(({ lockAddress, ordinal, groupSize }) => {
                  const details = detailsFor(lockAddress);
                  const label = nameOf(lockAddress) || web3.formatAddress(lockAddress);
                  return (
                    <option key={lockAddress} value={lockAddress} disabled={!details || details.cancelled}>
                      {`${label}${groupSize > 1 ? ` · grant ${ordinal} of ${groupSize}` : ''} — ${
                        details ? web3.formatAddress(details.beneficiary) : web3.formatAddress(lockAddress)
                      }${!details ? ' · not loaded' : details.cancelled ? ' · cancelled' : ''}`}
                    </option>
                  );
                })}
              </select>
              <div className="sh-cause">
                The grant, not the wallet: a holder with two grants moving one to a new address
                has to be able to say which. A cancelled grant cannot be reassigned at all.
                Use the grant page controls below to choose a grant on another page.
              </div>
            </div>
            <div className="sh-field">
              <label>New beneficiary</label>
              <input
                type="text"
                value={replacementBeneficiary}
                onChange={(e) => setReplacementBeneficiary(e.target.value)}
                placeholder="0x…"
                className="sh-input"
                required
              />
              <div className="sh-cause">
                The destination may already hold other grants — consolidating two onto one wallet
                is allowed. Reassigning goes through the lock's own beneficiary change, so a
                PAUSED grant must be unpaused first.
              </div>
            </div>
            <button
              type="submit"
              disabled={admin.isLoading || !reassignLockAddress || !replacementBeneficiary}
              className="sh-btn sh-btn-primary sh-btn-block"
              style={{ gridColumn: '1 / -1' }}
            >
              {admin.isLoading ? 'Reassigning…' : 'Reassign grant'}
              <Icon d={ICONS.arrowRight} size={14} />
            </button>
          </form>
        )}
      </div>

      {/* All Locks */}
      <div className="sh-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div className="sh-label">Grants</div>
            <div className="sh-h2" style={{ marginTop: 6 }}>
              All grants <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({admin.allLocks.length})</span>
            </div>
            <div className="sh-cause" style={{ marginTop: 6 }}>
              Only the current page is loaded. Grants are grouped by holder within this page;
              a holder may have more grants on other pages. Cancelled grants remain in the roster.
            </div>
          </div>
        </div>

        {admin.allLocks.length > 0 && (
          <div style={{ padding: '16px 24px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label>
              Grants per page{' '}
              <select className="sh-select" style={{ width: 'auto', display: 'inline-block' }}
                aria-label="Grants per page" value={grants.pageSize} onChange={(event) => {
                navigate('/admin', { replace: true });
                grants.setPageSize(Number(event.target.value));
              }}>
                {[10, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </label>
            <span aria-live="polite">
              Page {grants.page + 1} of {grants.pageCount} · Grants {grants.page * grants.pageSize + 1}–{Math.min((grants.page + 1) * grants.pageSize, admin.allLocks.length)} of {admin.allLocks.length}
            </span>
            <button type="button" className="sh-btn sh-btn-sm" disabled={grants.page === 0}
              onClick={() => changeGrantPage(grants.page - 1)}>Previous page</button>
            <button type="button" className="sh-btn sh-btn-sm" disabled={grants.page + 1 >= grants.pageCount}
              onClick={() => changeGrantPage(grants.page + 1)}>Next page</button>
            {grants.loading && <span role="status">Loading grant details…</span>}
            {grants.error && <div role="alert">{grants.error}{' '}
              <button type="button" className="sh-btn sh-btn-sm" onClick={grants.retry}>Retry details</button>
            </div>}
          </div>
        )}

        {admin.allLocks.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
            No grants created yet.
          </div>
        ) : (
          <div>
            {groupedLocks.map(({ lockAddress: lockAddr, ordinal, groupSize, isGroupStart }) => {
              const details = detailsFor(lockAddr);
              const isSelected = sameAddress(selectedLock, lockAddr);
              const label = nameOf(lockAddr);
              // A live grant can be resized at any point in its life, in either
              // direction: setTotalAmount moves the schedule and leaves the
              // ledger alone, so there is nothing for a client-side window to
              // protect. Cancellation is the only state that refuses
              // (LockIsCancelled), so it is the only thing gated on here.
              const canEditAmount = Boolean(details && !details.cancelled);
              // A live reading of what the typed figure would do, so the two
              // directions are visible before the confirmation rather than only
              // inside it. Same arithmetic the confirmation signs off on.
              const amountDraft = (amountInputs[lockAddr] ?? '').trim();
              const draftTotal =
                /^\d+$/.test(amountDraft) && BigInt(amountDraft) > BigInt(0)
                  ? BigInt(amountDraft) * BigInt(10 ** environment.tokenDecimals)
                  : null;
              const amountPreview =
                details && draftTotal !== null && draftTotal !== details.totalAmount
                  ? { next: draftTotal, ...projectResize(details, draftTotal) }
                  : null;
              const overpaid = details ? overpaidBy(details) : BigInt(0);
              const recipient = recipientInputs[lockAddr] ?? web3.address ?? '';
              // BYTES of UTF-8, the same thing the contract bounds. Never
              // String.length: 64 bytes is only about 21 CJK characters.
              const renameBytes = lockNameByteLength((renameInputs[lockAddr] ?? label).trim());

              return (
                <div
                  key={lockAddr}
                  style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid var(--border)',
                    // A second grant for the same holder hangs off the first
                    // rather than reading as an unrelated row.
                    borderTop: !isGroupStart ? '1px dashed var(--border)' : undefined,
                    marginTop: !isGroupStart ? -1 : undefined,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 16,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {/* The NAME leads. An address identifies a contract; only
                            the label says which grant this is, and with several
                            grants per wallet that is the question an operator is
                            actually asking. Where there is no label — a v9
                            factory, or a redacted one — the address takes the
                            lead rather than a placeholder standing in for it. */}
                        {label ? (
                          <>
                            <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
                            <span className="sh-mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              {web3.formatAddress(lockAddr)}
                            </span>
                          </>
                        ) : (
                          <span className="sh-mono" style={{ fontSize: 13 }}>
                            {web3.formatAddress(lockAddr)}
                          </span>
                        )}
                        {groupSize > 1 && (
                          <span
                            className="sh-pill"
                            title="Grants for this wallet on the current page. More may appear on other pages. Each grant has its own schedule, funding and cancellation."
                          >
                            Grant {ordinal} of {groupSize}
                          </span>
                        )}
                        {!details && <span className="sh-pill">{grants.loading ? 'Loading…' : 'Details unavailable'}</span>}
                        {details && (
                          <>
                            {/* Funding health, not "armed": under incremental
                                funding every lock is armed early and what matters
                                is whether the next tranche is covered. */}
                            <span
                              className={`sh-pill ${fundingHealth(details).cls}`}
                              title={fundingHealth(details).title}
                            >
                              {fundingHealth(details).text}
                            </span>
                            {/* Track is not a status, so it stays neutral next to the state pills. */}
                            <span className="sh-pill">{categoryLabel(details.category)}</span>
                            {details.isPaused && (
                              <span className="sh-pill sh-pill-danger">Paused</span>
                            )}
                            {/* Terminal, and it says what it cost: the beneficiary
                                lost everything they had not already claimed. */}
                            {details.cancelled && (
                              <span
                                className="sh-pill sh-pill-danger"
                                title="Cancelled: everything unclaimed was forfeited, including tokens already earned, and the lock's balance was swept. This cannot be reversed."
                              >
                                {/* What the beneficiary never receives, which is
                                    everything not already claimed. Not quoted as
                                    "forfeited by this cancellation": a grant that
                                    was off-boarded first had already lost part of
                                    it, and the exact split is only in the
                                    Cancelled event. */}
                                Cancelled ·{' '}
                                {web3.formatTokenAmount(
                                  details.totalAmount > details.unlockedAmount
                                    ? details.totalAmount - details.unlockedAmount
                                    : BigInt(0),
                                )}{' '}
                                unpaid
                              </span>
                            )}
                            {details.excessFunding > BigInt(0) && (
                              <span
                                className="sh-pill sh-pill-warn"
                                title="This lock holds more than it can ever pay out. Return excess in the expanded row is the only thing that can reach it."
                              >
                                Over-funded {web3.formatTokenAmount(details.excessFunding)}
                              </span>
                            )}
                            {/* Claimed above the payable CEILING, which a downward
                                resize and an off-board can each produce: the grant
                                was corrected below what had already gone out, or
                                frozen at a date whose curve sits below it. Stated
                                on the row because every derived figure clamps at
                                zero and would otherwise make this look like an
                                ordinary grant with nothing outstanding. */}
                            {overpaid > BigInt(0) && (
                              <span
                                className="sh-pill sh-pill-danger"
                                title="Claimed exceeds what this grant can ever pay: it was resized below what had already been paid out, or off-boarded at a date whose frozen curve sits below it. Nothing further will ever be paid from it, and the difference is only recoverable through emergencyWithdrawFrom."
                              >
                                Overpaid {web3.formatTokenAmount(overpaid)}
                              </span>
                            )}
                            {details.offboardTime > BigInt(0) && !details.cancelled && (
                              <span className="sh-pill sh-pill-danger">
                                Off-boarded {formatDateTime(new Date(Number(details.offboardTime) * 1000))}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {details && (
                        <div
                          style={{
                            marginTop: 8,
                            color: 'var(--text-muted)',
                            fontSize: 12,
                            display: 'flex',
                            gap: 18,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span>
                            <span className="sh-label" style={{ marginRight: 6 }}>Beneficiary</span>
                            <span className="sh-mono" style={{ color: 'var(--text-2)' }}>
                              {web3.formatAddress(details.beneficiary)}
                            </span>
                          </span>
                          <span>
                            <span className="sh-label" style={{ marginRight: 6 }}>Total</span>
                            <span className="sh-num" style={{ color: 'var(--text-2)' }}>
                              {web3.formatTokenAmount(details.totalAmount)} SAHARA
                            </span>
                          </span>
                          {/* The ceiling, which is NOT the allocation once a grant
                              has been frozen or cancelled. Shown only when the two
                              differ, so an ordinary row stays unchanged.
                              payableCeiling, never details.maxEntitlement: on a
                              v7/v9 factory the raw field falls back to totalAmount,
                              so reading it directly printed an off-boarded grant's
                              whole allocation here while the beneficiary's own
                              screen -- which has always used the clamp -- printed
                              the real, smaller figure. */}
                          {ceilingOf(details) !== details.totalAmount && (
                            <span>
                              <span className="sh-label" style={{ marginRight: 6 }}>Can still pay</span>
                              <span
                                className="sh-num"
                                style={{ color: details.cancelled ? 'var(--danger)' : 'var(--warn)' }}
                              >
                                {web3.formatTokenAmount(ceilingOf(details))} SAHARA
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {/* Funding is recurring, so this never disappears after the
                          first top-up. It arms the lock if needed and pays off
                          whatever is earned as of today; it moves nothing when the
                          lock is already square. */}
                      {details && !details.cancelled && admin.incrementalFunding !== null && (admin.incrementalFunding || !details.isInitialized) && (
                        <button
                          type="button"
                          onClick={() => promptInitializeLock(lockAddr)}
                          disabled={admin.isLoading}
                          className={`sh-btn sh-btn-sm ${
                            details.unfundedEarned > BigInt(0) || !details.isInitialized
                              ? 'sh-btn-primary'
                              : ''
                          }`}
                          title={
                            admin.incrementalFunding === false
                              ? 'Fund the full allocation and initialize this lock'
                              : details.unfundedEarned > BigInt(0)
                              ? `Send the ${web3.formatTokenAmount(
                                  details.unfundedEarned,
                                )} SAHARA this beneficiary has earned but cannot claim`
                              : 'Arm the lock and catch it up to today; nothing to send right now'
                          }
                        >
                          {admin.incrementalFunding === false ? 'Initialize lock' : details.isInitialized ? 'Top up now' : 'Arm & fund'}
                        </button>
                      )}
                      <button
                        type="button"
                        // Both edges replace. Expanding a row is a view detail,
                        // not a step worth a Back press, and pushing would strand
                        // a dead /admin entry per open/close cycle — Back would
                        // do nothing several times before leaving the section.
                        onClick={() =>
                          navigate(isSelected ? '/admin' : `/admin/lock/${lockAddr}`, {
                            replace: true,
                          })
                        }
                        className="sh-btn sh-btn-sm"
                      >
                        {isSelected ? 'Hide' : 'Details'}
                        <Icon d={isSelected ? ICONS.caret : ICONS.chevron} size={12} />
                      </button>
                    </div>
                  </div>

                  {isSelected && details && (
                    <div
                      style={{
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: '1px solid var(--border)',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 16,
                      }}
                    >
                      {details.cancelled && (
                        <div className="sh-banner sh-banner-danger" style={{ gridColumn: '1 / -1' }}>
                          <Icon d={ICONS.warn} size={16} stroke={1.6} />
                          <div>
                            <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                              This grant was cancelled
                            </div>
                            <div style={{ marginTop: 2 }}>
                              Everything the beneficiary had not already claimed is forfeit,
                              including tokens they had vested and earned but never collected, and
                              the lock's whole balance was swept in the same transaction. Only
                              what they had already taken —{' '}
                              {web3.formatTokenAmount(details.unlockedAmount)} SAHARA — was out of
                              reach. It cannot be reversed; a replacement grant is the only remedy.
                            </div>
                          </div>
                        </div>
                      )}
                      {/* The overpaid state, which a downward resize is allowed to
                          create. Every derived figure clamps at zero rather than
                          underflowing, so without this the row reads as a settled
                          grant with nothing outstanding. */}
                      {overpaid > BigInt(0) && (
                        <div className="sh-banner sh-banner-danger" style={{ gridColumn: '1 / -1' }}>
                          <Icon d={ICONS.warn} size={16} stroke={1.6} />
                          <div>
                            <div style={{ fontWeight: 500, color: 'var(--text)' }}>
                              Claimed exceeds this grant by{' '}
                              {web3.formatTokenAmount(overpaid)} SAHARA
                            </div>
                            <div style={{ marginTop: 2 }}>
                              {web3.formatTokenAmount(details.unlockedAmount)} SAHARA has been paid
                              out against a lifetime ceiling of{' '}
                              {web3.formatTokenAmount(ceilingOf(details))}
                              {ceilingOf(details) !== details.totalAmount
                                ? ` — the grant is recorded at ${web3.formatTokenAmount(
                                    details.totalAmount,
                                  )}, but off-boarding froze its curve below that`
                                : ', because the amount was corrected below what had already gone out'}
                              . That is a recorded state, not a fault: the ledger now states the
                              grant honestly. Nothing further will ever be payable from this lock,
                              its earned, claimable and outstanding figures are all zero by
                              arithmetic rather than by accident, and the tokens are in the
                              beneficiary's wallet — only emergencyWithdrawFrom can go after them,
                              and it is a separate transaction on purpose.
                            </div>
                          </div>
                        </div>
                      )}
                      <div>
                        <div className="sh-label">Grant name</div>
                        <div
                          style={{
                            fontSize: 13,
                            marginTop: 6,
                            color: label ? 'var(--text)' : 'var(--text-muted)',
                          }}
                        >
                          {label || 'No label set'}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Lock address</div>
                        <div className="sh-mono" style={{ fontSize: 12, marginTop: 6, wordBreak: 'break-all' }}>
                          {lockAddr}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Beneficiary</div>
                        <div className="sh-mono" style={{ fontSize: 12, marginTop: 6, wordBreak: 'break-all' }}>
                          {details.beneficiary}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Total amount</div>
                        <div className="sh-num" style={{ fontSize: 14, marginTop: 6 }}>
                          {web3.formatTokenAmount(details.totalAmount)}
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          The allocation as granted. What it can still PAY is below, and the two
                          differ once a grant is frozen or cancelled.
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Can still pay</div>
                        <div
                          className="sh-num"
                          style={{
                            fontSize: 14,
                            marginTop: 6,
                            color:
                              ceilingOf(details) === details.totalAmount
                                ? undefined
                                : details.cancelled
                                  ? 'var(--danger)'
                                  : 'var(--warn)',
                          }}
                        >
                          {web3.formatTokenAmount(ceilingOf(details))}
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          {details.cancelled
                            ? 'Zero: a cancelled grant pays nothing further, ever'
                            : overpaid > BigInt(0)
                              ? ceilingOf(details) !== details.totalAmount
                                ? 'Frozen at what had vested by the off-board date — and already ' +
                                  'below what has been claimed, so nothing further is payable'
                                : 'The lifetime ceiling — already exceeded by what has been claimed, ' +
                                  'so nothing further is payable from here'
                              : details.offboardTime > BigInt(0)
                                ? 'Frozen at what had vested by the off-board date; the rest is forfeit'
                                : 'The full allocation — nothing has reduced this ceiling'}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Vested</div>
                        <div className="sh-num" style={{ fontSize: 14, marginTop: 6 }}>
                          {web3.formatTokenAmount(displayedVestedAmount(details))}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Claimed</div>
                        <div
                          className="sh-num"
                          style={{
                            fontSize: 14,
                            marginTop: 6,
                            color: overpaid > BigInt(0) ? 'var(--danger)' : undefined,
                          }}
                        >
                          {web3.formatTokenAmount(details.unlockedAmount)}
                        </div>
                        {overpaid > BigInt(0) && (
                          <div className="sh-cause" style={{ marginTop: 6 }}>
                            {web3.formatTokenAmount(overpaid)} more than this grant can ever pay —{' '}
                            {ceilingOf(details) !== details.totalAmount
                              ? 'off-boarding froze its curve below what had already been paid'
                              : 'it was resized below what had already been paid'}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="sh-label">Claimable now</div>
                        <div className="sh-num" style={{ fontSize: 14, marginTop: 6 }}>
                          {web3.formatTokenAmount(details.availableAmount)}
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          What claim() pays today — earned, capped by the lock balance
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Unlock schedule</div>
                        <div className="sh-num" style={{ fontSize: 14, marginTop: 6 }}>
                          {web3.formatTokenAmount(details.unlockScheduleAmount)}
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          {categoryLabel(details.category)} track — 25% at the cliff, then 36
                          tranches every 30 days
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Unlock cliff</div>
                        <div style={{ fontSize: 13, marginTop: 6 }}>
                          {details.unlockCliffTime > BigInt(0)
                            ? formatDateTime(new Date(Number(details.unlockCliffTime) * 1000))
                            : 'Not scheduled'}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Start time</div>
                        <div style={{ fontSize: 13, marginTop: 6 }}>
                          {formatDateTime(new Date(Number(details.startTime) * 1000))}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Status</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                          <span
                            className={`sh-pill ${fundingHealth(details).cls}`}
                            title={fundingHealth(details).title}
                          >
                            {fundingHealth(details).text}
                          </span>
                          <span className={`sh-pill ${details.isPaused ? 'sh-pill-danger' : 'sh-pill-success'}`}>
                            {details.isPaused ? 'Paused' : 'Active'}
                          </span>
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          {details.isInitialized
                            ? 'Armed — claims are open once a tranche is both earned and funded'
                            : 'Not armed yet — claiming reverts until the first top-up arms it'}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Earned, unclaimed</div>
                        <div className="sh-num" style={{ fontSize: 14, marginTop: 6 }}>
                          {web3.formatTokenAmount(details.earnedUnclaimed)}
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          What the schedule owes, funded or not
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Held in lock</div>
                        <div
                          className="sh-num"
                          style={{
                            fontSize: 14,
                            marginTop: 6,
                            color: details.unfundedEarned > BigInt(0) ? 'var(--danger)' : undefined,
                          }}
                        >
                          {web3.formatTokenAmount(details.lockBalance)}
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          {details.unfundedEarned > BigInt(0)
                            ? `Short ${web3.formatTokenAmount(details.unfundedEarned)} against what is earned`
                            : 'Covers everything earned so far'}
                        </div>
                      </div>
                      <div>
                        <div className="sh-label">Next tranche</div>
                        <div style={{ fontSize: 13, marginTop: 6 }}>
                          {details.nextUnlockTime > BigInt(0)
                            ? formatDateTime(new Date(Number(details.nextUnlockTime) * 1000))
                            : 'None scheduled'}
                        </div>
                        <div className="sh-cause" style={{ marginTop: 6 }}>
                          {details.nextUnlockTime === BigInt(0)
                            ? 'Nothing further will be released'
                            : details.fundingDueNextUnlock > BigInt(0)
                              ? `Needs ${web3.formatTokenAmount(details.fundingDueNextUnlock)} more before then`
                              : 'Already funded for it'}
                        </div>
                      </div>
                      <div
                        style={{
                          gridColumn: '1 / -1',
                          borderTop: '1px solid var(--border)',
                          paddingTop: 16,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 14,
                        }}
                      >
                        <div className="sh-label">Off-boarding</div>
                        {/* Both controls disappear once a grant is cancelled: the
                            contract reverts LockIsCancelled on setOffboardTime,
                            INCLUDING the 0 that reactivates a schedule. That is
                            what makes cancellation terminal rather than a deeper
                            off-board, so offering either here would be offering a
                            transaction that cannot succeed. */}
                        {details.cancelled ? (
                          <div className="sh-cause">
                            Not available: this grant is cancelled. Off-boarding and reactivation
                            are both refused on it for good — cancellation already froze it at
                            zero and swept it.
                          </div>
                        ) : details.offboardTime > BigInt(0) ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              flexWrap: 'wrap',
                              justifyContent: 'space-between',
                            }}
                          >
                            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                              Vesting frozen at{' '}
                              <span className="sh-mono" style={{ color: 'var(--text)' }}>
                                {formatDateTime(new Date(Number(details.offboardTime) * 1000))}
                              </span>
                              .
                            </div>
                            <button
                              type="button"
                              onClick={() => promptReactivate(lockAddr)}
                              disabled={admin.isLoading}
                              className="sh-btn sh-btn-sm sh-btn-success"
                            >
                              Reactivate
                            </button>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: 10,
                              alignItems: 'center',
                            }}
                          >
                            <input
                              type="datetime-local"
                              value={offboardInputs[lockAddr] ?? ''}
                              onChange={(e) =>
                                setOffboardInputs((prev) => ({ ...prev, [lockAddr]: e.target.value }))
                              }
                              className="sh-input"
                            />
                            <button
                              type="button"
                              onClick={() => promptOffboard(lockAddr)}
                              disabled={admin.isLoading}
                              className="sh-btn sh-btn-sm sh-btn-danger"
                              title="Freeze vesting at the selected timestamp"
                            >
                              Off-board
                            </button>
                          </div>
                        )}

                        {/* Relabel. Not one-way, deliberately: an immutable label
                            makes a typo permanent and public. The empty string is
                            accepted on chain here and only here. */}
                        <div
                          style={{
                            paddingTop: 12,
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                          }}
                        >
                          <div className="sh-label">Name</div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: 10,
                              alignItems: 'center',
                            }}
                          >
                            <input
                              type="text"
                              value={renameInputs[lockAddr] ?? label}
                              onChange={(e) =>
                                setRenameInputs((prev) => ({ ...prev, [lockAddr]: e.target.value }))
                              }
                              placeholder="SEED-001"
                              className="sh-input"
                            />
                            <button
                              type="button"
                              onClick={() => promptRenameLock(lockAddr)}
                              disabled={admin.isLoading || renameBytes > MAX_LOCK_NAME_BYTES}
                              className="sh-btn sh-btn-sm"
                            >
                              Rename
                            </button>
                          </div>
                          <div
                            className="sh-cause"
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 12,
                              color: renameBytes > MAX_LOCK_NAME_BYTES ? 'var(--danger)' : undefined,
                            }}
                          >
                            <span>
                              Changes the label only — schedule, funding and tokens are untouched.
                              Blanking it is allowed for redaction, but redaction is not removal:
                              the old name stays in the event log and on every archive node.
                            </span>
                            <span className="sh-mono" style={{ whiteSpace: 'nowrap' }}>
                              {renameBytes} / {MAX_LOCK_NAME_BYTES} bytes
                            </span>
                          </div>
                        </div>

                        {/* Resize a grant. Available at any point in its life and in
                            either direction, because the ledger and the schedule are
                            separate: unlockedAmount is never touched and the curve
                            rescales around it. Both directions reach BACKWARDS,
                            which is the whole reason the preview and the
                            confirmation price them in this grant's own numbers. */}
                        <div
                          style={{
                            paddingTop: 12,
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                          }}
                        >
                          <div className="sh-label">Grant amount</div>
                          {canEditAmount ? (
                            <>
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr auto',
                                  gap: 10,
                                  alignItems: 'center',
                                }}
                              >
                                <input
                                  type="number"
                                  min={0}
                                  value={amountInputs[lockAddr] ?? ''}
                                  onChange={(e) =>
                                    setAmountInputs((prev) => ({ ...prev, [lockAddr]: e.target.value }))
                                  }
                                  placeholder={`${web3.formatTokenAmount(details.totalAmount)} today`}
                                  className="sh-input"
                                />
                                <button
                                  type="button"
                                  onClick={() => promptSetLockTotalAmount(lockAddr)}
                                  disabled={admin.isLoading || !(amountInputs[lockAddr] ?? '').trim()}
                                  className="sh-btn sh-btn-sm"
                                >
                                  Change amount
                                </button>
                              </div>
                              {/* What the typed figure would do, in tokens, before
                                  the confirmation is even opened. */}
                              {amountPreview && (
                                <div
                                  className="sh-cause"
                                  style={{
                                    color:
                                      amountPreview.overpaidBy > BigInt(0)
                                        ? 'var(--danger)'
                                        : amountPreview.becomesOwed > BigInt(0) ||
                                            amountPreview.behindBy > BigInt(0)
                                          ? 'var(--warn)'
                                          : undefined,
                                  }}
                                >
                                  {amountPreview.next > details.totalAmount
                                    ? amountPreview.becomesOwed > BigInt(0)
                                      ? `Raising to ${web3.formatTokenAmount(amountPreview.next)} makes ` +
                                        `${web3.formatTokenAmount(amountPreview.becomesOwed)} SAHARA owed ` +
                                        `immediately — the curve rescales over the past too — claimable ` +
                                        `as soon as this lock is funded for it.`
                                      : `Raising to ${web3.formatTokenAmount(amountPreview.next)}. Nothing ` +
                                        `has been released on this schedule yet, so nothing falls due today.`
                                    : amountPreview.overpaidBy > BigInt(0)
                                      ? `Cutting to ${web3.formatTokenAmount(amountPreview.next)} takes what ` +
                                        `this grant can ever pay to ` +
                                        `${web3.formatTokenAmount(amountPreview.ceilingAfter)} — BELOW the ` +
                                        `${web3.formatTokenAmount(details.unlockedAmount)} already claimed. ` +
                                        `Nothing further would ever be paid, and the grant would read as ` +
                                        `overpaid by ${web3.formatTokenAmount(amountPreview.overpaidBy)}.`
                                      : amountPreview.owedAfter === BigInt(0) &&
                                          amountPreview.behindBy > BigInt(0)
                                        ? `Cutting to ${web3.formatTokenAmount(amountPreview.next)} rescales the ` +
                                          `past too: the smaller curve is ` +
                                          `${web3.formatTokenAmount(amountPreview.behindBy)} SAHARA behind what ` +
                                          `has already been claimed, so nothing further is paid until it ` +
                                          `catches up — years, not months.`
                                        : `Cutting to ${web3.formatTokenAmount(amountPreview.next)} leaves ` +
                                          `${web3.formatTokenAmount(amountPreview.owedAfter)} SAHARA owed right ` +
                                          `now, down from ${web3.formatTokenAmount(amountPreview.owedNow)}.`}
                                </div>
                              )}
                              <div className="sh-cause">
                                Editable at any point in this grant's life, in either direction.
                                The ledger is never touched — what has been claimed stays claimed —
                                but the schedule rescales around it, THE PAST INCLUDED: a raise
                                falls due immediately, and a cut pauses payment until the smaller
                                curve catches up, or forever if it lands below what has already
                                been paid. Whole tokens.
                              </div>
                            </>
                          ) : (
                            <div className="sh-cause">
                              Not available: this grant is cancelled, and setTotalAmount reverts
                              LockIsCancelled on it for good. Every other grant can be resized at
                              any time, in either direction; the remedy for a mistaken cancellation
                              is a replacement grant.
                            </div>
                          )}
                        </div>

                        {/* Recovery and cancellation. Both send tokens somewhere, so
                            they share one recipient field and it is named in the
                            confirmation before anything is signed. */}
                        <div
                          style={{
                            paddingTop: 12,
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                          }}
                        >
                          <div className="sh-label">Recovery</div>
                          <div className="sh-field">
                            <label>Send recovered tokens to</label>
                            <input
                              type="text"
                              value={recipient}
                              onChange={(e) =>
                                setRecipientInputs((prev) => ({ ...prev, [lockAddr]: e.target.value }))
                              }
                              placeholder="0x…"
                              className="sh-input"
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 12,
                              flexWrap: 'wrap',
                            }}
                          >
                            <div className="sh-cause" style={{ maxWidth: 520 }}>
                              {details.excessFunding > BigInt(0)
                                ? `${web3.formatTokenAmount(
                                    details.excessFunding,
                                  )} SAHARA is stranded in this lock above anything it can ever pay. Funding only ever pushes, so nothing but Return excess can reach it.`
                                : 'Nothing is stranded in this lock: it holds no more than it can still pay out.'}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                type="button"
                                onClick={() => promptReturnLockExcess(lockAddr)}
                                disabled={admin.isLoading || details.excessFunding === BigInt(0)}
                                className="sh-btn sh-btn-sm"
                                title={
                                  details.excessFunding === BigInt(0)
                                    ? 'There is nothing stranded to return; the contract refuses a zero recovery rather than costing gas for nothing'
                                    : 'Move only what this lock can never pay out'
                                }
                              >
                                Return excess
                              </button>
                              <button
                                type="button"
                                onClick={() => promptCancelLock(lockAddr)}
                                disabled={admin.isLoading || details.cancelled}
                                className="sh-btn sh-btn-danger sh-btn-sm"
                                title={
                                  details.cancelled
                                    ? 'Already cancelled — this happens once'
                                    : 'Permanent full clawback, and it sweeps the balance in the same transaction'
                                }
                              >
                                {details.cancelled ? 'Cancelled' : 'Cancel grant'}
                              </button>
                            </div>
                          </div>
                          {!details.cancelled && (
                            <div className="sh-cause">
                              Cancelling is PERMANENT and it is full clawback: the beneficiary
                              forfeits everything they have not already claimed, including tokens
                              they had earned but never collected, and the lock's whole balance is
                              swept to the address above in the same transaction. The grant then
                              leaves the funding plan but stays in this roster forever. Note what
                              it silences — a merely swept lock keeps reporting its debt, a
                              cancelled one does not, so the LockCancelled and EmergencyWithdraw
                              events are the only remaining signal.
                            </div>
                          )}
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 8,
                            paddingTop: 12,
                            borderTop: '1px solid var(--border)',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => promptPauseLock(lockAddr)}
                            disabled={admin.isLoading || details.isPaused}
                            className="sh-btn sh-btn-danger sh-btn-sm"
                          >
                            Pause lock
                          </button>
                          <button
                            type="button"
                            onClick={() => promptUnpauseLock(lockAddr)}
                            disabled={admin.isLoading || !details.isPaused}
                            className="sh-btn sh-btn-success sh-btn-sm"
                          >
                            Unpause lock
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
